import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { MessageCircle, Mic, Sparkles, Send, BookOpen, FileText, Microscope, ArrowLeft, Eye, X, History, ExternalLink, GripVertical, PanelLeft, PanelLeftClose, Square, Plus, Pause, Play, ChevronDown, ChevronUp, Layers, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';
import TextbookViewer from '@/components/TextbookViewer';
import BudleeAIChatPanel from '@/components/BudleeAIChatPanel';
import ChatHistoryPanel from '@/components/ChatHistoryPanel';
import PDFViewerModal from '@/components/PDFViewerModal';
import PDFSectionViewer from '@/components/PDFSectionViewer';
import LearningPath from '@/components/LearningPath';
import StatsBar from '@/components/StatsBar';
import MediaGallery from '@/components/MediaGallery';
import InteractiveQuiz from '@/components/InteractiveQuiz';
import SimulationRenderer from '@/components/simulations/SimulationRenderer';
import BudleeAIIcon from '@/components/BudleeAIIcon';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken, getPortalType, getUserData } from '@/utils/auth';
import { subjectsAPI, chaptersAPI } from '@/api/curriculum';
import { createChatSession, sendChatMessage, sendChatMessageStream, getOrCreateSession, getChatSession, updateSessionExplanationMode, cancelChatGeneration } from '@/api/chat';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { trackEvent, attachUserProps } from '@/analytics/analytics';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import { usePoints } from '@/context/PointsContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkLatexSanitizer from '@/utils/remarkLatexSanitizer';
import rehypeKatex from 'rehype-katex';
import MermaidRenderer from '@/components/MermaidRenderer';
import { initKaTeX } from '@/utils/katexSetup';
import 'katex/dist/katex.min.css';
import { sanitizeChatMarkdown } from '@/utils/chatMarkdownSanitizer';
import { makeStreamingMarkdownSafe } from '@/utils/streamingMarkdown';
import { useSpeechToText } from '@/hooks/useSpeechToText';
import { coerceToPlainText } from '@/utils/coerceToPlainText';

// Use the exact same sanitizer as the regular Chat page so LaTeX repair stays consistent.
const formatAnswerForDisplay = (content, { streaming = false } = {}) => {
  const sanitized = sanitizeChatMarkdown(content, { streaming });
  return streaming ? makeStreamingMarkdownSafe(sanitized) : sanitized;
};

// Helper: auto-wrap raw Mermaid source in a code block if not already
function autoWrapMermaid(content) {
  if (typeof content !== 'string') return content;
  // Detect common Mermaid diagram patterns not inside a code block
  // e.g. "mermaid flowchart TD" or "mermaid\nflowchart TD"
  const mermaidPattern = /(^|\n)\s*mermaid\s+flowchart\s+TD[\s\S]*?(?=\n\s*\n|$)/gi;
  let replaced = content;
  replaced = replaced.replace(mermaidPattern, (match) => {
    // If already in a code block, skip
    if (/```mermaid[\s\S]*?```/i.test(match)) return match;
    // Otherwise, wrap in code block
    return `\n\n\u0060\u0060\u0060mermaid\n${match.trim().replace(/^mermaid\s*/i, '')}\n\u0060\u0060\u0060\n`;
  });
  return replaced;
}

// Extract interactive quiz payloads so we don't dump raw JSON to the UI
const parseInteractiveContent = (content) => {
  const safe = typeof content === 'string' ? content : String(content ?? '');
  // Matches: new line + (optional md prefixes) + Interactive Activity + (newline/end/colon)
  const headerRegex = /(?:^|[\n\r])(?:[\t >#*\-`]*)(?:Interactive Activity)(?:[\t >#*\-`]*)(?:\n|$|:)/im;
  const match = safe.match(headerRegex);
  if (!match) return { cleanContent: safe, quizData: null, simulation: null };

  const cleanContent = safe.substring(0, match.index).trim();
  const tail = safe.substring(match.index);
  let quizData = null;
  let simulation = null;

  const firstBrace = tail.indexOf('{');
  if (firstBrace !== -1) {
    const lastBrace = tail.lastIndexOf('}');
    if (lastBrace > firstBrace) {
      try {
        const jsonCand = tail.substring(firstBrace, lastBrace + 1);
        const parsed = JSON.parse(jsonCand);
        if (parsed) {
          if (parsed.simulation_type || parsed.simulationType) {
            const simulationType = parsed.simulation_type || parsed.simulationType;
            const simulationData = parsed.simulation_data || parsed.simulationData;
            if (simulationType && simulationData) {
              simulation = { simulationType, simulationData, title: parsed.title, description: parsed.description };
            }
          } else if (Array.isArray(parsed.parts)) {
            simulation = { simulationType: 'interactive_diagram', simulationData: parsed };
          } else if (parsed.type || parsed.question) {
            quizData = parsed;
          }
        }
      } catch {
        const codeBlockMatch = tail.match(/```json\s*([\s\S]*?)\s*```/);
        if (codeBlockMatch && codeBlockMatch[1]) {
          try {
            const parsed = JSON.parse(codeBlockMatch[1]);
            if (parsed) {
              if (parsed.simulation_type || parsed.simulationType) {
                const simulationType = parsed.simulation_type || parsed.simulationType;
                const simulationData = parsed.simulation_data || parsed.simulationData;
                if (simulationType && simulationData) {
                  simulation = { simulationType, simulationData, title: parsed.title, description: parsed.description };
                }
              } else if (Array.isArray(parsed.parts)) {
                simulation = { simulationType: 'interactive_diagram', simulationData: parsed };
              } else {
                quizData = parsed;
              }
            }
          } catch { }
        }
      }
    }
  }

  return { cleanContent, quizData, simulation };
};
/**
 * PRODUCTION MARKDOWN+MATH RENDERER
 * 
 * Philosophy: TRUST THE BACKEND
 * - Backend (LaTeXFormatter.py) does comprehensive formatting
 * - Frontend only does minimal safety checks
 * - Typewriter effect for UX (like ChatGPT)
 */
// Updated MarkdownMath component with Frontend Defense Layer
// Updated MarkdownMath with "Final Defense" Sanitization Layer
// Updated MarkdownMath with Spacing Injector & Structure Formatter
const MarkdownMath = ({ children, messageId, className, citations = [], pageSources = [], autoInjectCitations = false, onCitationClick, streaming = false }) => {
  const [renderKey, setRenderKey] = useState(0);
  const [katexReady, setKatexReady] = useState(false);

  useEffect(() => {
    const checkKatexReady = () => {
      const katexStyles = document.querySelector('link[href*="katex"]') ||
        document.querySelector('style[data-emotion*="katex"]');

      if (katexStyles) {
        setKatexReady(true);
        return true;
      }
      return false;
    };

    if (checkKatexReady()) {
      setRenderKey(prev => prev + 1);
      return;
    }

    const timer = setInterval(() => {
      if (checkKatexReady()) {
        clearInterval(timer);
        setRenderKey(prev => prev + 1);
      }
    }, 100);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (katexReady) setRenderKey(prev => prev + 1);
  }, [messageId, katexReady]);

  const propsRef = useRef({ onCitationClick });
  propsRef.current = { onCitationClick };

  const memoizedComponents = useMemo(() => {
    // Helper to replace unicode superscripts AND inline page citations with styled badges
    const replaceSuperscripts = (text) => {
      if (!text) return null;

      // ── Step 1: Split on page citation patterns  ──────────────────────────────
      // Matches: [p. 6], p. 6, [pg. 6], pg. 6, [p. 6-7], p. 6, p. 7 (consecutive)
      const PAGE_CITATION_RE = /(\[?(?:p(?:g)?\.?\s*\d+(?:\s*[,\-]\s*(?:p(?:g)?\.?\s*)?\d+)*)\]?)/g;

      const expandWithPageCitations = (inputText) => {
        const chunks = inputText.split(PAGE_CITATION_RE);
        if (chunks.length === 1) return [inputText]; // no citations found
        return chunks;
      };

      // ── Step 2: Split on unicode superscripts (legacy support) ───────────────
      const parts = text.split(/([¹²³⁴⁵⁶⁷⁸⁹⁰]+)/g);
      const processedParts = Array.isArray(parts) ? parts : [text];

      return processedParts.flatMap((part, i) => {
        if (React.isValidElement(part)) return [part];
        if (typeof part !== 'string') return [part];

        // ── Step 3: Process page citations within each text chunk ────────────
        const citationChunks = expandWithPageCitations(part);

        return citationChunks.flatMap((chunk, ci) => {
          if (React.isValidElement(chunk)) return [chunk];
          if (typeof chunk !== 'string') return [chunk];

          // Check if this chunk is a page citation
          if (/^(\[?(?:p(?:g)?\.?\s*\d+(?:\s*[,\-]\s*(?:p(?:g)?\.?\s*)?\d+)*)\]?)$/.test(chunk.trim()) && /\d/.test(chunk)) {
            const displayLabel = chunk.replace(/^\[|\]$/g, '').trim(); // strip outer brackets
            // Extract page numbers for click handler
            const pageNums = [...chunk.matchAll(/\d+/g)].map(m => parseInt(m[0], 10));
            const firstPage = pageNums[0] ?? null;

            return [(
              <span
                key={`${i}-pg-${ci}`}
                className="inline-flex items-center gap-0.5 mx-0.5 px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground text-[11px] font-medium cursor-pointer hover:bg-[hsl(var(--primary))]/15 hover:text-[hsl(var(--primary))] transition-colors select-none border border-border align-baseline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (propsRef.current.onCitationClick && firstPage) {
                    propsRef.current.onCitationClick({ page: firstPage });
                  }
                }}
                title={firstPage ? `Go to page ${firstPage} in textbook` : 'View in textbook'}
              >
                {displayLabel}
              </span>
            )];
          }

          // ── Step 4: Check for Figure references ─────────────────────────────
          const figureRegex = /((?:Figures?|Figs?\.?)\s+\d+(?:[.\-]\d+)?)/g;
          const subParts = chunk.split(figureRegex);
          if (subParts.length === 1) return [chunk];

          return subParts.map((subPart, j) => {
            if (subPart.match(/^(?:Figures?|Figs?\.?)\s+\d+(?:[.\-]\d+)?$/)) {
              return (
                <span
                  key={`${i}-${ci}-${j}`}
                  className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded-md bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-xs font-semibold cursor-pointer hover:bg-[hsl(var(--primary))]/20 transition-colors select-none border border-[hsl(var(--primary))]/20"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (propsRef.current.onCitationClick) {
                      propsRef.current.onCitationClick({
                        figure: subPart,
                        page: null
                      });
                    }
                  }}
                  title="View Figure in Textbook"
                >
                  <Eye className="w-3 h-3" />
                  {subPart}
                </span>
              );
            }
            return subPart;
          });
        });
      }).flat();
    };

    const processChildren = (nodeChildren) => {
      return React.Children.map(nodeChildren, child => {
        if (typeof child === 'string') {
          return replaceSuperscripts(child);
        }
        if (React.isValidElement(child) && child.props.children) {
          return React.cloneElement(child, {
            children: processChildren(child.props.children)
          });
        }
        return child;
      });
    };

    const renderAutoCitation = (originalChildren) => {
      if (!autoInjectCitations || !Array.isArray(pageSources) || pageSources.length === 0) return null;
      // Get text content to check if it already has a citation
      const textContent = React.Children.toArray(originalChildren)
        .map(c => typeof c === 'string' ? c : (c?.props?.children !== undefined ? String(c.props.children) : ''))
        .join('');
      if (/p(?:g)?\.?\s*\d+/i.test(textContent)) return null;
      // Deterministically pick a page to avoid hydration/render mismatches
      const pageNum = pageSources[Math.max(0, textContent.length % pageSources.length)];
      return (
        <span
          className="inline-flex items-center gap-0.5 ml-1.5 px-1.5 py-0.5 rounded-lg bg-muted/60 text-muted-foreground text-[10px] font-medium cursor-pointer hover:bg-[hsl(var(--primary))]/15 hover:text-[hsl(var(--primary))] transition-all select-none border border-border/60 align-baseline opacity-80 hover:opacity-100 shadow-sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (propsRef.current.onCitationClick) {
              propsRef.current.onCitationClick({ page: pageNum });
            }
          }}
          title={`Go to page ${pageNum} in textbook`}
        >
          p. {pageNum}
        </span>
      );
    };

    return {
      p: ({ children: pChildren, ...props }) => (
        <p {...props} className="my-4 leading-7 break-words">
          {processChildren(pChildren)}
          {renderAutoCitation(pChildren)}
        </p>
      ),
      li: ({ children: liChildren, ...props }) => (
        <li {...props}>
          {processChildren(liChildren)}
          {renderAutoCitation(liChildren)}
        </li>
      ),
      pre: ({ className: preClassName, ...props }) => (
        <pre
          {...props}
          className={cn(
            preClassName,
            'max-w-full overflow-x-hidden whitespace-pre-wrap break-words'
          )}
          style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
        />
      ),
      code: ({ inline, className: codeClassName, children: codeChildren, ...props }) => {
        const match = /language-(\w+)/.exec(codeClassName || '');
        const lang = match?.[1]?.toLowerCase?.();

        if (!inline && lang === 'mermaid') {
          const chart = String(codeChildren || '').replace(/\n$/, '');
          return (
            <div className="my-4">
              <MermaidRenderer chart={chart} />
            </div>
          );
        }

        return (
          <code
            {...props}
            className={cn(
              codeClassName,
              'max-w-full whitespace-pre-wrap break-words'
            )}
            style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
            data-inline={inline ? 'true' : 'false'}
          >
            {codeChildren}
          </code>
        );
      },
    };
  }, []);

  // Parse interactive content first
  const { cleanContent, quizData, simulation } = parseInteractiveContent(children);

  // Apply sanitizer and auto-wrap Mermaid diagrams for visual output
  let processedContent = formatAnswerForDisplay(cleanContent);
  processedContent = autoWrapMermaid(processedContent);
  if (streaming) processedContent = makeStreamingMarkdownSafe(processedContent);

  return (
    <div
      className={cn(
        "text-[15px] leading-7 text-foreground min-w-0 max-w-full break-words",
        "font-['Inter',-apple-system,system-ui,sans-serif]",

        // Headings (polished section headers)
        "[&_h1]:mt-6 [&_h1]:mb-3 [&_h1]:text-lg [&_h1]:md:text-xl [&_h1]:font-bold [&_h1]:tracking-tight",
        "[&_h2]:mt-6 [&_h2]:mb-2.5 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:underline [&_h2]:underline-offset-4 [&_h2]:decoration-[hsl(var(--primary))]/35",
        "[&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:underline [&_h3]:underline-offset-4 [&_h3]:decoration-[hsl(var(--primary))]/25",

        // Emphasis
        "[&_strong]:font-semibold [&_strong]:text-foreground",
        "[&_em]:italic",

        // Spacing & Lists
        "[&_p]:my-4 [&_p]:leading-7",
        "[&_p]:break-words",
        "[&_ul]:my-3 [&_ul]:pl-5 [&_ul]:space-y-1 [&_ul]:list-disc [&_ul]:list-outside",
        "[&_ol]:my-3 [&_ol]:pl-5 [&_ol]:space-y-1 [&_ol]:list-decimal [&_ol]:list-outside",
        "[&_li::marker]:text-muted-foreground",

        // Wrapping / overflow guards (prevents chat bubble overflow)
        // Prefer wrapping over horizontal scrolling for readability inside chat bubbles.
        "[&_pre]:max-w-full [&_pre]:overflow-x-hidden [&_pre]:whitespace-pre-wrap [&_pre]:break-words",
        "[&_pre_code]:whitespace-pre-wrap [&_pre_code]:break-words",
        "[&_code]:max-w-full [&_code]:break-words [&_code]:whitespace-pre-wrap",
        "[&_a]:break-words",

        // Tables
        "[&_table]:my-4 [&_table]:w-full [&_table]:border-collapse",
        "[&_table]:border [&_table]:border-[hsl(var(--card-border))]",
        "[&_th]:border [&_th]:border-[hsl(var(--card-border))] [&_th]:bg-[hsl(var(--card-bg))] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-foreground",
        "[&_td]:border [&_td]:border-[hsl(var(--card-border))] [&_td]:px-3 [&_td]:py-2 [&_td]:align-top",

        // Separators
        "[&_hr]:my-6 [&_hr]:border-[hsl(var(--card-border))]",

        // --- TEXTBOOK DISPLAY MATH FIX ---

        "[&_.katex-display]:block",
        "[&_.katex-display]:w-full",
        "[&_.katex-display]:max-w-none",
        "[&_.katex-display]:text-center",

        // Escape chat bubble padding
        // (Avoid negative margins; they can overflow the chat bubble)
        "[&_.katex-display]:mx-0",

        // Textbook sizing
        "[&_.katex-display]:text-[1.5em]",
        "[&_.katex-display]:font-semibold",

        // Visual polish
        "[&_.katex-display]:my-8",
        "[&_.katex-display]:py-5",
        "[&_.katex-display]:px-6",
        "[&_.katex-display]:rounded-xl",
        "[&_.katex-display]:overflow-x-auto",
        "[&_.katex-display]:bg-[hsl(var(--card-bg))]",
        "[&_.katex-display]:border",
        "[&_.katex-display]:border-[hsl(var(--card-border))]",

        // Fraction visibility
        "[&_.frac-line]:border-b-[1.5px]",
        "[&_.frac-line]:border-current",

        className,

      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
        rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: false, trust: true }]]}
        components={memoizedComponents}
      >
        {processedContent}
      </ReactMarkdown>

      {/* Embedded Interactive Elements */}
      {simulation && (
        <div className="mt-4 border border-[hsl(var(--primary))]/20 rounded-xl overflow-hidden shadow-sm bg-[hsl(var(--card-bg))]">
          <SimulationRenderer
            type={simulation.simulationType || simulation.type}
            data={simulation.simulationData || simulation.data || simulation}
            title={simulation.title}
            description={simulation.description}
          />
        </div>
      )}

      {quizData && (
        <div className="mt-4">
          <InteractiveQuiz quiz={quizData} />
        </div>
      )}
    </div>
  );
};

const CitedMessageContent = ({ content, messageId, pageSources = [], autoInjectCitations = false, onCitationClick = null, streaming = false }) => {
  // Sources/citations UI is a placeholder for later; keep answers clean for now.
  return (
    <MarkdownMath
      messageId={messageId}
      className="text-xs sm:text-sm"
      pageSources={pageSources}
      autoInjectCitations={autoInjectCitations}
      onCitationClick={onCitationClick}
      streaming={streaming}
    >
      {content}
    </MarkdownMath>
  );
};


export default function ChatPage({ onNavigateToChapter, onXPEarned, onAskBudleeAI }) {
  const {
    selectedSubject,
    selectedChapter,
    selectedSubjectId,
    selectedChapterId,
    updateSubject,
    updateChapter,
  } = useSubjectChapter();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [viewingChapter, setViewingChapter] = useState(false);
  const [prefilledText, setPrefilledText] = useState('');
  const [budleeAIContextText, setBudleeAIContextText] = useState('');
  const [budleeAIContextAnchor, setBudleeAIContextAnchor] = useState(null);
  const [budleeAIPrefillInput, setBudleeAIPrefillInput] = useState('');
  const [budleeAIPrefillAndSend, setBudleeAIPrefillAndSend] = useState('');
  const [explanationType, setExplanationType] = useState('Textbook Explanation');
  const [pendingModeSwitch, setPendingModeSwitch] = useState(null); // { mode: string } when confirmation is needed
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryText, setSummaryText] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const messageContainerRefById = useRef(new Map());
  const chatScrollContainerRef = useRef(null);
  const isUserNearBottomRef = useRef(true);
  const forceScrollNextRef = useRef(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const loadingAnchorRef = useRef(null);
  const chatInputRef = useRef(null);
  const speakNextReplyRef = useRef(false);
  const handleSendMessageRef = useRef(null);
  const [isTtsSpeaking, setIsTtsSpeaking] = useState(false);
  const [isTtsPaused, setIsTtsPaused] = useState(false);
  const [isReadingSelection, setIsReadingSelection] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const regenerationAbortRef = useRef(null);

  // Track whether the user is near the bottom of the main chat scroll area.
  // If the user scrolls up, avoid auto-scrolling new messages into view.
  useEffect(() => {
    const el = chatScrollContainerRef.current;
    if (!el) return;

    const thresholdPx = 220;
    const updateNearBottom = () => {
      try {
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        const nearBottom = distanceFromBottom <= thresholdPx;
        isUserNearBottomRef.current = nearBottom;
        if (nearBottom) setShowJumpToLatest(false);
      } catch {
        // ignore
      }
    };

    updateNearBottom();
    el.addEventListener('scroll', updateNearBottom, { passive: true });
    return () => el.removeEventListener('scroll', updateNearBottom);
  }, []);

  // Mobile split state: top = PDF/content, bottom = chat panel
  const [mobileShowPdf, setMobileShowPdf] = useState(true);
  const [mobileJourneyFullscreen, setMobileJourneyFullscreen] = useState(false);
  // Mobile collapse states
  const [textbookViewerCollapsed, setTextbookViewerCollapsed] = useState(false);
  const [chatPanelCollapsed, setChatPanelCollapsed] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isSelectionExpanded, setIsSelectionExpanded] = useState(false); // Mobile selection panel state

  const handleMediaClick = (type, data) => {
    setSelectedMedia({ type, data });
  };

  const closeMediaModal = () => {
    setSelectedMedia(null);
  };

  const handleTabChange = (messageId, tab) => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, activeTab: tab } : msg
    ));
  };



  const speakBudleeReply = (rawText) => {
    // ... existing speech logic ...
    if (typeof window === 'undefined') return;
    if (!('speechSynthesis' in window)) {
      // ...
      return;
    }
    // ...
  };


  const stopBudleeAudio = () => {
    try {
      window.speechSynthesis?.cancel?.();
    } catch { }
    setIsTtsSpeaking(false);
    setIsTtsPaused(false);
  };

  const pauseBudleeAudio = () => {
    try {
      if (window.speechSynthesis?.speaking && !window.speechSynthesis?.paused) {
        window.speechSynthesis.pause();
        setIsTtsPaused(true);
      }
    } catch { }
  };

  const resumeBudleeAudio = () => {
    try {
      if (window.speechSynthesis?.paused) {
        window.speechSynthesis.resume();
        setIsTtsPaused(false);
      }
    } catch { }
  };



  const {
    isSupported: isVoiceConversationSupported,
    isListening: isVoiceConversationListening,
    toggle: toggleVoiceConversation,
    stop: stopVoiceConversation,
  } = useSpeechToText({
    lang: 'en-US',
    interimResults: false,
    continuous: false,
    onFinalTranscript: (finalText) => {
      const text = String(finalText || '').trim();
      if (!text) return;
      speakNextReplyRef.current = true;
      handleSendMessageRef.current?.(text);
    },
    onError: (err) => {
      const msg = err?.message || 'Speech recognition error';
      toast.error('Voice conversation failed', { description: msg });
      speakNextReplyRef.current = false;
    },
  });

  const {
    isSupported: isSpeechToTextSupported,
    isListening: isSpeechToTextListening,
    toggle: toggleSpeechToText,
    stop: stopSpeechToText,
  } = useSpeechToText({
    lang: 'en-US',
    onFinalTranscript: (finalText) => {
      setInputValue((prev) => {
        const trimmed = String(prev || '').trim();
        return trimmed ? `${trimmed} ${finalText}` : finalText;
      });
      try {
        chatInputRef.current?.focus?.();
      } catch { }
    },
    onError: (err) => {
      const msg = err?.message || 'Speech recognition error';
      toast.error('Voice-to-Text failed', { description: msg });
    },
  });

  const handleSpeechToTextClick = () => {
    if (!isSpeechToTextSupported) {
      toast.error('Voice-to-Text not supported', {
        description: 'Try Chrome/Edge on HTTPS (or localhost).',
      });
      return;
    }
    if (isVoiceConversationListening) stopVoiceConversation();
    toggleSpeechToText();
  };

  const handleVoiceConversationClick = () => {
    if (!isVoiceConversationSupported) {
      toast.error('Voice conversation not supported', {
        description: 'Try Chrome/Edge on HTTPS (or localhost).',
      });
      return;
    }
    // Avoid competing recognizers.
    if (isSpeechToTextListening) stopSpeechToText();
    // If stopping, also stop any ongoing TTS.
    if (isVoiceConversationListening) {
      speakNextReplyRef.current = false;
      try {
        window.speechSynthesis?.cancel?.();
      } catch { }
    }
    toggleVoiceConversation();
  };

  // Stop any audio when leaving this page
  useEffect(() => {
    return () => {
      speakNextReplyRef.current = false;
      try {
        stopSpeechToText();
        stopVoiceConversation();
      } catch { }
      stopBudleeAudio();
    };
  }, [stopSpeechToText, stopVoiceConversation]);

  // Persist/restore the currently active chat session across refresh
  const ACTIVE_CHAT_STATE_KEY = 'chat_active_state_v1';
  const safeJsonParse = (raw, fallback) => {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch {
      return fallback;
    }
  };
  const getActiveChatStorageId = () => {
    try {
      const p = getPortalType(window.location.pathname || '');
      const user = getUserData(p) || {};
      const userKey = user.id || user.user_id || user.username || user.email || 'unknown';
      return `${p}|${userKey}`;
    } catch {
      return 'unknown|unknown';
    }
  };

  // Ensure textarea height is correct when value changes programmatically
  useEffect(() => {
    try {
      const ta = chatInputRef.current;
      if (ta) {
        ta.style.height = 'auto';
        ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
      }
    } catch { }
  }, [inputValue]);
  const isLoadingFromHistoryRef = useRef(false);
  const loadingSessionIdRef = useRef(null);
  const isRestoringFromStorageRef = useRef(false);
  const suppressSessionIdFallbackRef = useRef(false);
  const prevSelectionRef = useRef({ subjectId: null, chapterId: null });
  const [userFullName, setUserFullName] = useState('You');

  // AbortController for cancelling requests
  const abortControllerRef = useRef(null);

  // ========================================
  // Initialize KaTeX (ONCE on app load)
  // ========================================
  useEffect(() => {
    initKaTeX();
  }, []);
  const portal = getPortalType(window.location.pathname || '');
  const pdfAuthToken = getAuthToken(portal);

  // PDF Viewer State
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [mobilePdfViewerOpen, setMobilePdfViewerOpen] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState(null);
  // Always start PDF previews at page 1 on refresh/switch.
  // We still persist `pdfViewerPage` for potential future use, but we do not restore it automatically.
  const [pdfViewerPage, setPdfViewerPage] = useState(1);
  const [pdfViewerTitle, setPdfViewerTitle] = useState('');
  const [pdfViewerTargetFigure, setPdfViewerTargetFigure] = useState(null);
  const [pdfViewerBbox, setPdfViewerBbox] = useState(null);
  const [pdfViewerSection, setPdfViewerSection] = useState(null);

  // Temporary citation highlight (persists until next question)
  const [citationHighlights, setCitationHighlights] = useState([]);
  const [citationHighlightedId, setCitationHighlightedId] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPaneWidth, setPdfPaneWidth] = useState(() => {
    try {
      const raw = localStorage.getItem('chat_pdf_pane_width');
      const n = raw == null ? NaN : Number(raw);
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  });

  // (PDF text selection actions are handled by PDFSectionViewer)
  const splitContainerRef = useRef(null);
  const isDraggingDividerRef = useRef(false);
  const [showLearningPathModal, setShowLearningPathModal] = useState(false);

  // When switching subjects, hide/clear PDF preview so chat occupies full width.
  // The PDF pane can be re-opened once a relevant chapter is selected.
  const prevSubjectIdRef = useRef(null);
  useEffect(() => {
    const prev = prevSubjectIdRef.current;
    const next = selectedSubjectId ?? null;
    prevSubjectIdRef.current = next;

    // Skip first mount
    if (prev == null) return;
    if (prev === next) return;

    // Close/hide preview and clear last PDF so we don't show previous subject's PDF.
    setShowPdfPreview(false);
    setPdfViewerOpen(false);
    setPdfViewerUrl(null);
    setPdfViewerTitle('');
    setPdfViewerBbox(null);
    setPdfViewerPage(1);
  }, [selectedSubjectId]);

  // Backend data
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [chaptersData, setChaptersData] = useState({});
  const [loadingSubjects, setLoadingSubjects] = useState(true);

  // Load user full name
  useEffect(() => {
    const portal = getPortalType(window.location.pathname || '');
    const userData = getUserData(portal);
    if (userData) {
      const fullName = userData.full_name ||
        `${userData.first_name || ''} ${userData.last_name || ''}`.trim() ||
        userData.username ||
        'You';
      setUserFullName(fullName);
    }
  }, []);

  // Fetch subjects and chapters from backend
  useEffect(() => {
    const loadSubjectsAndChapters = async () => {
      try {
        const userData = getUserData(getPortalType(window.location.pathname || ''));
        if (!userData || !userData.board || !userData.grade) {
          setLoadingSubjects(false);
          return;
        }

        const boardId = userData.board.id;
        const gradeId = userData.grade.id;

        // Fetch subjects
        const subjectsList = await subjectsAPI.list({ board: boardId, grade: gradeId });
        const subjectsFormatted = subjectsList.map(s => ({
          value: s.id.toString(),
          label: s.subject_name,
          id: s.id
        }));
        setSubjects(subjectsFormatted);
        setLoadingSubjects(false);
      } catch (error) {
        console.error('Error loading subjects:', error);
        setLoadingSubjects(false);
      }
    };

    loadSubjectsAndChapters();
  }, []);

  // Fetch chapters when subject is selected
  useEffect(() => {
    const loadChapters = async () => {
      if (!selectedSubjectId) {
        setChapters([]);
        setChaptersData({});
        return;
      }

      try {
        const chaptersList = await chaptersAPI.list({ subject: selectedSubjectId });
        const chaptersFormatted = chaptersList.map(c => ({
          value: c.id.toString(),
          label: c.chapter_name,
          id: c.id,
          file_url: c.file_url,
          file_upload: c.file_upload
        }));
        setChapters(chaptersFormatted);

        // Store full chapter data for PDF access
        const chaptersMap = {};
        chaptersList.forEach(c => {
          chaptersMap[c.id] = c;
        });
        setChaptersData(chaptersMap);
      } catch (error) {
        console.error('Error loading chapters:', error);
        setChapters([]);
      }
    };

    loadChapters();
  }, [selectedSubjectId]);

  // Update suggested queries when chapter is selected with animation
  useEffect(() => {
    const updateTopics = async () => {
      // Start animation
      setTopicsAnimating(true);

      // Small delay to show fade-out
      await new Promise(resolve => setTimeout(resolve, 200));

      if (selectedChapterId && chaptersData[selectedChapterId]) {
        const chapterData = chaptersData[selectedChapterId];
        // Check if chapter has suggested_chat_topics
        if (chapterData.suggested_chat_topics && Array.isArray(chapterData.suggested_chat_topics) && chapterData.suggested_chat_topics.length > 0) {
          // Use chapter-specific topics with typing animation
          const newTopics = chapterData.suggested_chat_topics.slice(0, 4);
          setSuggestedQueries(newTopics);
        } else {
          // Fallback to default topics
          setSuggestedQueries([
            "What is Newton's First Law of Motion?",
            "Explain force and its effects",
            "What is the relationship between force, mass, and acceleration?",
            "Give examples of balanced and unbalanced forces"
          ]);
        }
      } else {
        // Reset to default when no chapter selected
        setSuggestedQueries([
          "What is Newton's First Law of Motion?",
          "Explain force and its effects",
          "What is the relationship between force, mass, and acceleration?",
          "Give examples of balanced and unbalanced forces"
        ]);
      }

      // End animation after fade-in
      setTimeout(() => setTopicsAnimating(false), 300);
    };

    updateTopics();
  }, [selectedChapterId, chaptersData]);

  // Get lesson data for selected chapter - use backend PDF URL
  const getLessonData = () => {
    if (!selectedChapterId || !chaptersData[selectedChapterId]) {
      return null;
    }

    const chapterData = chaptersData[selectedChapterId];

    // Check if the chapter actually has a PDF before constructing the URL
    // The backend serializer provides file_url (if PDF exists) and is_ready status.
    // We check file_url/file_upload to see if a file is attached.
    const hasPdf = !!(chapterData.file_url || chapterData.file_upload || chapterData.is_ready);

    // Get PDF URL from backend - use proxy endpoint like old UI
    // only if we believe a PDF exists.
    const pdfUrl = hasPdf ? apiUrl(`superadmin/chapters/${selectedChapterId}/pdf/`) : null;

    // Create a lesson object with the required properties for TextbookViewer
    return {
      id: selectedChapterId,
      title: chapterData.chapter_name,
      fullTitle: chapterData.chapter_name,
      pdfUrl: pdfUrl
    };
  };

  const openPdfPreview = ({ page = null, bbox = null, smooth = false, sectionName = null, targetFigure = null, autoShow = true } = {}) => {
    const lessonData = getLessonData();
    if (!lessonData?.pdfUrl) {
      toast.error('Textbook PDF not available');
      return;
    }

    // Only change page if one is explicitly provided.
    // Figure-only clicks often do NOT include a page; forcing page=1 breaks UX.
    const hasPage = page !== null && page !== undefined && String(page).trim() !== '';
    const pageNum = hasPage ? Math.max(1, parseInt(page, 10) || 1) : null;

    setPdfViewerUrl(lessonData.pdfUrl);
    if (pageNum) setPdfViewerPage(pageNum);
    setPdfViewerTitle(lessonData.title);
    setPdfViewerSection(sectionName);
    // Allow re-triggering the same figure by bouncing through null.
    if (targetFigure) {
      const nextKey = String(targetFigure).trim().toLowerCase();
      const curKey = String(pdfViewerTargetFigure || '').trim().toLowerCase();
      if (nextKey && nextKey === curKey) {
        setPdfViewerTargetFigure(null);
        setTimeout(() => setPdfViewerTargetFigure(targetFigure), 0);
      } else {
        setPdfViewerTargetFigure(targetFigure);
      }
    } else {
      setPdfViewerTargetFigure(null);
    }

    // Store bbox for highlighting (will be passed to PDF viewer)
    if (bbox && Array.isArray(bbox) && bbox.length === 4) {
      // Store in a ref or state for the PDF viewer to use
      setPdfViewerBbox(bbox);
    } else {
      setPdfViewerBbox(null);
    }

    // Always use the in-page PDF preview pane for page navigation.
    // This avoids opening the standalone/modal PDF viewer when the user clicks
    // page references inside the chat response.
    if (autoShow) {
      setShowPdfPreview(true);
    }

    // Optional smooth scroll only if explicitly requested and preview was hidden
    if (smooth && !showPdfPreview && autoShow) {
      // Use requestAnimationFrame for instant visual update, then smooth scroll
      requestAnimationFrame(() => {
        const previewElement = document.querySelector('[data-pdf-preview]');
        if (previewElement) {
          previewElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    }
  };

  const clearCitationHighlight = useCallback(() => {
    setCitationHighlights([]);
    setCitationHighlightedId(null);
  }, []);

  const handleCitationClick = useCallback(
    (src) => {
      // Allow if page is present OR if figure is present
      if (!src) return;
      if (!src.page && !src.figure) return;

      const highlight = src.highlight || {};
      const highlightId = Date.now();
      const parsedPage = src.page !== null && src.page !== undefined && String(src.page).trim() !== ''
        ? parseInt(src.page, 10)
        : null;
      const pageNum = Number.isFinite(parsedPage) ? Math.max(1, parsedPage) : null;

      // Open PDF preview at the cited page
      openPdfPreview({ page: pageNum, smooth: true, sectionName: src.section_name, targetFigure: src.figure });

      // Apply a persistent text highlight ONLY when a valid page is provided.
      // For figure-only clicks, rely on figure search + arrow (highlighting page 1 is misleading).
      if (pageNum) {
        const highlightToApply = {
          id: highlightId,
          page_number: pageNum,
          text: highlight.text || '',
          section_name: highlight.section_name || '',
          context_before: highlight.context_before || '',
          context_after: highlight.context_after || '',
          disable_remove: true,
        };

        console.log('🎨 Applying highlight:', highlightToApply);
        setCitationHighlights([highlightToApply]);
        setCitationHighlightedId(highlightId);
      } else {
        setCitationHighlights([]);
        setCitationHighlightedId(null);
      }
    },
    [openPdfPreview]
  );

  // Dev-only: allow triggering figure/citation highlighting from the console.
  // See `window.figureHighlightTest` (installed in development via `src/debug/figureHighlightTest.js`).
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    const handler = (evt) => {
      const detail = evt?.detail || {};
      const mode = detail.mode;
      const figure = detail.figure || null;
      const page = detail.page ?? null;
      const section_name = detail.section_name ?? null;

      console.log('[FIGURE_HIGHLIGHT_TEST] received:', { mode, figure, page, section_name });

      if (!figure && !page) return;
      handleCitationClick({
        figure,
        page,
        section_name,
        highlight: {},
      });
    };

    window.addEventListener('FIGURE_HIGHLIGHT_TEST', handler);
    return () => window.removeEventListener('FIGURE_HIGHLIGHT_TEST', handler);
  }, [handleCitationClick]);

  // Divider drag handlers (YouLearn-like resize) - Fixed to work smoothly
  useEffect(() => {
    const onMove = (e) => {
      if (!isDraggingDividerRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const root = splitContainerRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const minLeft = 280;
      const minRight = 420;
      const maxLeft = Math.max(minLeft, rect.width - minRight);
      const next = Math.min(Math.max(x, minLeft), maxLeft);
      setPdfPaneWidth(next);
    };
    const onUp = (e) => {
      if (!isDraggingDividerRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      isDraggingDividerRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };
    // Use capture phase to ensure events are caught even when mouse moves over other elements
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mouseup', onUp, true);
    // Also handle mouseleave to stop dragging if mouse leaves window
    const onMouseLeave = () => {
      if (isDraggingDividerRef.current) {
        isDraggingDividerRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.body.style.pointerEvents = '';
      }
    };
    document.addEventListener('mouseleave', onMouseLeave);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseup', onUp, true);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  useEffect(() => {
    try {
      if (typeof pdfPaneWidth === 'number') {
        localStorage.setItem('chat_pdf_pane_width', String(pdfPaneWidth));
      }
    } catch { }
  }, [pdfPaneWidth]);

  // Default split: 50/50 when preview is enabled and we don't yet have a stored width.
  useEffect(() => {
    if (!showPdfPreview) return;
    if (typeof pdfPaneWidth === 'number') return;
    const root = splitContainerRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    if (!rect.width) return;
    const half = rect.width / 2;
    const clamped = Math.min(Math.max(half, 320), 900);
    setPdfPaneWidth(clamped);
  }, [showPdfPreview, pdfPaneWidth]);

  /* Removed localStorage persistence for Textbook View toggle to ensure it is off by default.
  useEffect(() => {
    try {
      localStorage.setItem('chat_show_pdf_preview', String(showPdfPreview));
    } catch { }
  }, [showPdfPreview]);
  */

  // Default behavior: when a chapter is selected and preview is ON, auto-open the PDF pane (desktop)
  useEffect(() => {
    if (!selectedChapterId) return;
    if (!showPdfPreview) return;
    if (viewingChapter) return; // chapter view already has textbook viewer
    // Wait for chaptersData to be populated before trying to open PDF
    if (!chaptersData[selectedChapterId]) return;
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      openPdfPreview({ page: pdfViewerPage || 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChapterId, chaptersData, showPdfPreview]);

  // New behavior: when a chapter is selected (desktop), auto-open the PDF pane.
  // This prevents showing an unrelated PDF after a subject change, while still
  // giving the user the related PDF as soon as they pick a chapter.
  useEffect(() => {
    if (!selectedChapterId) return;
    if (viewingChapter) return;
    if (!chaptersData[selectedChapterId]) return;
    if (typeof window === 'undefined') return;
    if (window.innerWidth < 1024) return; // mobile uses modal via explicit open

    // Load the chapter's PDF immediately but do NOT auto-open the preview pane (autoShow: false).
    // This allows the user to toggle it manually.
    openPdfPreview({ page: pdfViewerPage || 1, autoShow: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChapterId, chaptersData, viewingChapter]);

  const handleViewChapter = () => {
    if (!selectedChapterId) {
      toast.error('Please select a chapter first');
      return;
    }
    setShowLearningPathModal(true);
  };

  const handleViewSummary = async () => {
    if (!selectedChapterId) {
      toast.error('Please select a chapter first');
      return;
    }

    setShowSummaryModal(true);
    setSummaryError(null);
    setSummaryText(null);

    try {
      setSummaryLoading(true);
      const portal = getPortalType(window.location.pathname || '');
      const token = getAuthToken(portal);

      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await axios.get(
        apiUrl(`core/chapters/${selectedChapterId}/summary/`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const summaryPoints = response.data?.summary_points;
      const rawSummary = response.data?.summary;

      let resolvedSummary = null;
      if (typeof rawSummary === 'string' && rawSummary.trim().length > 0) {
        resolvedSummary = rawSummary.trim();
      } else if (Array.isArray(summaryPoints) && summaryPoints.length > 0) {
        resolvedSummary = summaryPoints
          .map((point, idx) => `${idx + 1}. ${point}`)
          .join('\n\n');
      }

      if (!resolvedSummary) {
        throw new Error('Summary not available for this chapter.');
      }

      setSummaryText(resolvedSummary);
    } catch (error) {
      console.error('Error fetching summary:', error);
      const fallbackMessage = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to load summary';
      setSummaryError(fallbackMessage);
      setSummaryText(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleCloseChapterView = () => {
    setViewingChapter(false);
  };

  const handleAskBudleeAIWithText = (selectedText, _teacherNoteId = null, anchor = null) => {
    // Ask Budlee/Budlee AI should NOT fill the main chat editor context.
    // Keep a dedicated context value for the BudleeAIChatPanel.
    setBudleeAIContextText(coerceToPlainText(selectedText));
    setBudleeAIContextAnchor(anchor);
  };

  // Suggested queries - will be loaded from chapter data
  const [suggestedQueries, setSuggestedQueries] = useState([
    "What is Newton's First Law of Motion?",
    "Explain force and its effects",
    "What is the relationship between force, mass, and acceleration?",
    "Give examples of balanced and unbalanced forces"
  ]);
  const [topicsAnimating, setTopicsAnimating] = useState(false);

  // Keep the newest response visible from the top (avoid jumping to the bottom
  // which makes users miss the beginning of long answers).
  useEffect(() => {
    const el = isLoading
      ? loadingAnchorRef.current
      : (() => {
        const last = Array.isArray(messages) && messages.length ? messages[messages.length - 1] : null;
        const lastId = last?.id;
        if (!lastId) return null;
        return messageContainerRefById.current.get(lastId);
      })();
    if (!el) return;

    const shouldAutoScroll = forceScrollNextRef.current || isUserNearBottomRef.current;
    if (!shouldAutoScroll) {
      setShowJumpToLatest(true);
      return;
    }

    forceScrollNextRef.current = false;
    setShowJumpToLatest(false);
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch {
      // ignore
    }
  }, [messages.length, isLoading]);

  // Check for sessionId from navigation state (when resuming from history)
  useEffect(() => {
    const checkForResumedSession = async () => {
      // Check if we're resuming a session from history
      const state = window.history.state;
      const resumeSessionId = state?.sessionId;

      // If not resuming from history state, try restoring from localStorage (refresh case)
      let sessionIdToLoad = resumeSessionId;
      if (!sessionIdToLoad) {
        try {
          const store = safeJsonParse(localStorage.getItem(ACTIVE_CHAT_STATE_KEY) || '{}', {});
          const entry = store[getActiveChatStorageId()];
          if (entry?.sessionId) {
            sessionIdToLoad = entry.sessionId;
          }
        } catch { }
      }

      if (sessionIdToLoad) {
        try {
          setSessionLoading(true);
          isLoadingFromHistoryRef.current = true;
          isRestoringFromStorageRef.current = !resumeSessionId;
          loadingSessionIdRef.current = sessionIdToLoad;

          const session = await getChatSession(sessionIdToLoad);
          setCurrentSessionId(session.session_id);

          // Update subject/chapter from session if available
          if (session.subject) {
            updateSubject(session.subject, session.subject_name || '');
          }
          if (session.chapter) {
            updateChapter(session.chapter, session.chapter_name || '');
          }

          // Load messages first
          if (session.messages && session.messages.length > 0) {
            const formattedMessages = session.messages.map(msg => {
              const baseContent = msg.role === 'user' ? msg.content : (msg.answer || msg.content);
              const mode = msg.source_type || 'beyond_book';
              const mediaObj = msg.media || null;

              const result = {
                id: msg.message_id,
                type: msg.role === 'user' ? 'user' : 'ai',
                content: baseContent,
                key_points: msg.key_points,
                suggested_questions: msg.suggested_questions,
                page_sources: msg.page_sources,
                source_type: msg.source_type,
                media: mediaObj, // Include media (images/videos)
                timestamp: msg.created_at || msg.timestamp
              };

              if (msg.role === 'assistant') {
                result.answer = baseContent;
                result[`answer_${mode}_v2`] = baseContent;
                if (msg.key_points) result[`key_points_${mode}_v2`] = msg.key_points;
                if (msg.suggested_questions) result[`suggested_questions_${mode}_v2`] = msg.suggested_questions;
                if (msg.page_sources) result[`page_sources_${mode}_v2`] = msg.page_sources;
                result[`source_type_${mode}_v2`] = msg.source_type;
                if (mediaObj) result[`media_${mode}_v2`] = mediaObj;
              }
              return result;
            });
            setMessages(formattedMessages);
          }

          // Set explanation mode from the latest message or session
          let modeToRestore = null;
          if (session.messages && session.messages.length > 0) {
            const latestAiMsg = session.messages.slice().reverse().find(m => m.role === 'assistant');
            if (latestAiMsg && latestAiMsg.source_type) {
              const reverseModeMap = {
                'textbook': 'Textbook Explanation',
                'detailed': 'Detailed Explanation',
                'beyond_book': 'Beyond the Book'
              };
              modeToRestore = reverseModeMap[latestAiMsg.source_type];
            }
          }

          if (!modeToRestore && session.explanation_mode) {
            const explanationModeMap = {
              'textbook': 'Textbook Explanation',
              'detailed': 'Detailed Explanation',
              'beyond_book': 'Beyond the Book'
            };
            modeToRestore = explanationModeMap[session.explanation_mode] || 'Textbook Explanation';
          }

          if (modeToRestore) {
            setTimeout(() => {
              // Only restore from history if the user hasn't already manually changed the mode
              // or if we are currently at the default "Textbook Explanation"
              setExplanationType(prev => {
                // GUARD: Only restore state from history if:
                // 1. Current state is the default "Textbook" (meaning user likely hasn't picked yet)
                // 2. OR we are loading a different session entirely
                // 3. AND the target mode is actually different
                if (prev === 'Textbook Explanation' || prev === modeToRestore) {
                  return modeToRestore;
                }
                return prev;
              });
              // Keep flags set longer to ensure useEffect doesn't run
              setTimeout(() => {
                isLoadingFromHistoryRef.current = false;
                loadingSessionIdRef.current = null;
              }, 500);
            }, 500);
          } else {
            setTimeout(() => {
              isLoadingFromHistoryRef.current = false;
              loadingSessionIdRef.current = null;
            }, 500);
          }

          // Clear state to prevent reloading
          if (resumeSessionId) {
            window.history.replaceState({}, '');
          }
        } catch (error) {
          console.error('Error loading resumed session:', error);
          toast.error('Failed to load chat session');
          isLoadingFromHistoryRef.current = false;
          loadingSessionIdRef.current = null;
          isRestoringFromStorageRef.current = false;
        } finally {
          setSessionLoading(false);
        }
        return; // Don't create new session if resuming
      }
    };

    checkForResumedSession();
  }, []);

  // Create or get session when subject/chapter/explanation type changes
  useEffect(() => {
    const initializeSession = async () => {
      if (!selectedSubjectId || !selectedChapterId) {
        return;
      }

      // Skip if we just loaded a resumed session
      const state = window.history.state;
      if (state?.sessionId) {
        return;
      }

      // Skip if we're currently loading from history
      if (isLoadingFromHistoryRef.current) {
        return;
      }

      // Skip if we're restoring from localStorage (refresh)
      if (isRestoringFromStorageRef.current) {
        return;
      }

      // Skip if we're currently loading a specific session from history
      if (loadingSessionIdRef.current) {
        return;
      }

      // Skip if we already have a current session ID - this means we're loading from history
      // Only proceed if we don't have a session ID yet (new session creation)
      if (currentSessionId) {
        // If we have a session ID, verify it matches the current context before proceeding
        // This prevents creating a new session when loading from history
        return;
      }

      try {
        setSessionLoading(true);
        const explanationModeMap = {
          'Textbook Explanation': 'textbook',
          'Detailed Explanation': 'detailed',
          'Beyond the Book': 'beyond_book'
        };

        const session = await getOrCreateSession({
          subject_id: selectedSubjectId,
          chapter_id: selectedChapterId,
          page_context: 'chat',
          explanation_mode: explanationModeMap[explanationType] || 'textbook'
        });

        // Only set session ID if we don't already have one (to avoid overwriting history-loaded session)
        if (!currentSessionId) {
          setCurrentSessionId(session.session_id);
          try { trackEvent('chat_conversation_started', attachUserProps({ session_id: session.session_id, subject_id: selectedSubjectId, chapter_id: selectedChapterId, page: 'chat' })); } catch (e) { }
        }

        // Set explanation mode from session if available
        if (session.explanation_mode) {
          const explanationModeMap = {
            'textbook': 'Textbook Explanation',
            'detailed': 'Detailed Explanation',
            'beyond_book': 'Beyond the Book'
          };
          const mode = explanationModeMap[session.explanation_mode] || 'Textbook Explanation';
          // Only update if different to avoid triggering useEffect again
          if (mode !== explanationType) {
            setExplanationType(mode);
          }
        }

        // Load existing messages if session was retrieved
        if (session.messages && session.messages.length > 0) {
          const formattedMessages = session.messages.map(msg => {
            const baseContent = msg.role === 'user' ? msg.content : (msg.answer || msg.content);
            const mode = msg.source_type || 'beyond_book';
            const mediaObj = msg.media || null;

            const result = {
              id: msg.message_id,
              type: msg.role === 'user' ? 'user' : 'ai',
              content: baseContent,
              key_points: msg.key_points,
              suggested_questions: msg.suggested_questions,
              page_sources: msg.page_sources,
              source_type: msg.source_type,
              media: mediaObj, // Include media (images/videos)
              timestamp: msg.created_at || msg.timestamp
            };

            if (msg.role === 'assistant') {
              result.answer = baseContent;
              result[`answer_${mode}_v2`] = baseContent;
              if (msg.key_points) result[`key_points_${mode}_v2`] = msg.key_points;
              if (msg.suggested_questions) result[`suggested_questions_${mode}_v2`] = msg.suggested_questions;
              if (msg.page_sources) result[`page_sources_${mode}_v2`] = msg.page_sources;
              result[`source_type_${mode}_v2`] = msg.source_type;
              if (mediaObj) result[`media_${mode}_v2`] = mediaObj;
            }
            return result;
          });
          setMessages(formattedMessages);

          // Align the UI mode with the latest AI message's source_type
          const latestAiMsg = session.messages.slice().reverse().find(m => m.role === 'assistant');
          if (latestAiMsg && latestAiMsg.source_type) {
            const reverseModeMap = {
              'textbook': 'Textbook Explanation',
              'detailed': 'Detailed Explanation',
              'beyond_book': 'Beyond the Book'
            };
            const modeToRestore = reverseModeMap[latestAiMsg.source_type];
            if (modeToRestore) {
              setExplanationType(modeToRestore);
            }
          }
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error('Error initializing session:', error);
        toast.error('Failed to initialize chat session');
      } finally {
        setSessionLoading(false);
      }
    };

    initializeSession();
  }, [selectedSubjectId, selectedChapterId, explanationType, currentSessionId]);

  useEffect(() => {
    if (currentSessionId) {
      isRestoringFromStorageRef.current = false;
      suppressSessionIdFallbackRef.current = false;
    }
  }, [currentSessionId]);

  // When subject/chapter changes, start a fresh chat (new session + empty messages).
  useEffect(() => {
    const prev = prevSelectionRef.current;
    const next = { subjectId: selectedSubjectId ?? null, chapterId: selectedChapterId ?? null };

    const hadPrevSelection = prev.subjectId != null || prev.chapterId != null;
    const changed =
      hadPrevSelection &&
      (prev.subjectId !== next.subjectId || prev.chapterId !== next.chapterId);

    prevSelectionRef.current = next;

    if (!changed) return;

    // Don't reset while we're restoring/loading an existing session.
    if (isRestoringFromStorageRef.current) return;
    if (isLoadingFromHistoryRef.current) return;
    if (loadingSessionIdRef.current) return;

    // Prevent persistence from reusing the previous sessionId during this transition.
    suppressSessionIdFallbackRef.current = true;

    // Clear current chat state; initializeSession() will create a new session.
    setMessages([]);
    setCurrentSessionId(null);
    setIsLoading(false);
    setInputValue('');
    setPrefilledText('');
    setBudleeAIContextText('');
    setBudleeAIPrefillInput('');
    setBudleeAIPrefillAndSend('');
  }, [selectedSubjectId, selectedChapterId]);

  // Persist active session + UI state so refresh keeps current chat + PDF preview
  useEffect(() => {
    try {
      const id = getActiveChatStorageId();
      const store = safeJsonParse(localStorage.getItem(ACTIVE_CHAT_STATE_KEY) || '{}', {});

      const prevEntry = store[id] || {};
      const prevByChapter = prevEntry.pdfPageByChapterId && typeof prevEntry.pdfPageByChapterId === 'object'
        ? prevEntry.pdfPageByChapterId
        : {};
      const nextByChapter = { ...prevByChapter };
      if (selectedChapterId != null && pdfViewerPage) {
        nextByChapter[String(selectedChapterId)] = pdfViewerPage;
      }

      store[id] = {
        ...prevEntry,
        sessionId: suppressSessionIdFallbackRef.current ? (currentSessionId || null) : (currentSessionId || store[id]?.sessionId || null),
        subjectId: selectedSubjectId ?? store[id]?.subjectId ?? null,
        chapterId: selectedChapterId ?? store[id]?.chapterId ?? null,
        explanationType: explanationType ?? store[id]?.explanationType ?? null,
        pdfPage: pdfViewerPage || store[id]?.pdfPage || 1,
        pdfPageByChapterId: nextByChapter,
        showPdfPreview: !!showPdfPreview,
        pdfPaneWidth: typeof pdfPaneWidth === 'number' ? pdfPaneWidth : (store[id]?.pdfPaneWidth ?? null),
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(ACTIVE_CHAT_STATE_KEY, JSON.stringify(store));
    } catch { }
  }, [currentSessionId, selectedSubjectId, selectedChapterId, explanationType, pdfViewerPage, showPdfPreview, pdfPaneWidth]);

  // When chapter changes, always start the preview at page 1.
  useEffect(() => {
    if (selectedChapterId == null) return;
    setPdfViewerPage(1);
  }, [selectedChapterId]);

  const normalizeMediaPayload = (resp) => {
    const media = resp?.media;
    const images = Array.isArray(media?.images) ? media.images : (Array.isArray(resp?.images) ? resp.images : []);
    const videos = Array.isArray(media?.videos) ? media.videos : (Array.isArray(resp?.videos) ? resp.videos : []);
    return { images, videos };
  };

  // Caching: Watch for explosionType mode changes and swap the active context message natively
  const applyCachedModeToMessages = useCallback((level) => {
    const explanationModeMap = {
      'Textbook Explanation': 'textbook',
      'Detailed Explanation': 'detailed',
      'Beyond the Book': 'beyond_book'
    };
    const activeMode = explanationModeMap[level] || 'textbook';
    const answerKey = `answer_${activeMode}_v2`;
    const keyPointsKey = `key_points_${activeMode}_v2`;
    const suggestedQuestionsKey = `suggested_questions_${activeMode}_v2`;
    const pageSourcesKey = `page_sources_${activeMode}_v2`;
    const pageSourcesDetailedKey = `page_sources_detailed_${activeMode}_v2`;
    const sourceTypeKey = `source_type_${activeMode}_v2`;
    const mediaKey = `media_${activeMode}_v2`;
    const showCuriosityInviteKey = `show_curiosity_invite_${activeMode}_v2`;

    setMessages((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((msg) => {
        // Skip user messages or messages that don't have a cached answer for this mode
        if (!msg || msg.type === 'user' || !msg[answerKey]) return msg;
        changed = true;
        return {
          ...msg,
          // Apply cached properties back to the active bubble variables
          content: msg[answerKey], // ChatPage uses message.content for rendering
          answer: msg[answerKey],
          key_points: msg[keyPointsKey] || [],
          suggested_questions: msg[suggestedQuestionsKey] || msg.suggested_questions || [],
          page_sources: msg[pageSourcesKey] ?? msg.page_sources ?? null,
          page_sources_detailed: msg[pageSourcesDetailedKey] ?? msg.page_sources_detailed ?? null,
          source_type: msg[sourceTypeKey] ?? msg.source_type ?? null,
          show_curiosity_invite: msg[showCuriosityInviteKey] ?? msg.show_curiosity_invite ?? false,
          media: msg[mediaKey] ?? msg.media ?? { images: msg.images || [], videos: msg.videos || [] },
        };
      });
      return changed ? next : prev;
    });
  }, []);

  // Sync mode changes with backend - simplified to NOT trigger regeneration
  const prevExplanationTypeRef = useRef(explanationType);
  useEffect(() => {
    if (!currentSessionId || !explanationType) return;

    // Mapping for backend
    const explanationModeMap = {
      'Textbook Explanation': 'textbook',
      'Detailed Explanation': 'detailed',
      'Beyond the Book': 'beyond_book'
    };
    const activeMode = explanationModeMap[explanationType] || 'textbook';

    // Only update backend session preference for FUTURE messages
    updateSessionExplanationMode(currentSessionId, activeMode).catch(console.warn);
    prevExplanationTypeRef.current = explanationType;
  }, [explanationType, currentSessionId]);

  const getModeKeys = (mode) => ({
    answerKey: `answer_${mode}_v2`,
    keyPointsKey: `key_points_${mode}_v2`,
    suggestedQuestionsKey: `suggested_questions_${mode}_v2`,
    pageSourcesKey: `page_sources_${mode}_v2`,
    pageSourcesDetailedKey: `page_sources_detailed_${mode}_v2`,
    sourceTypeKey: `source_type_${mode}_v2`,
    mediaKey: `media_${mode}_v2`,
    showCuriosityInviteKey: `show_curiosity_invite_${mode}_v2`,
  });

  const handleMessageModeToggle = async (messageId, targetMode) => {
    const msg = messages.find(m => m.id === messageId);
    if (!msg || msg.source_type === targetMode || msg.status === 'regenerating') return;

    // Fix Edge Case 2: Sync global explanationType dropdown immediately
    const reverseModeMap = {
      'textbook': 'Textbook Explanation',
      'detailed': 'Detailed Explanation',
      'beyond_book': 'Beyond the Book'
    };
    setExplanationType(reverseModeMap[targetMode] || 'Textbook Explanation');

    const keys = getModeKeys(targetMode);

    // 1. Check if we already have it cached locally in React State
    if (msg[keys.answerKey]) {
      setMessages(prev => prev.map(m => {
        if (m.id !== messageId) return m;
        return {
          ...m,
          display_mode: targetMode, // Track display mode locally
          content: msg[keys.answerKey],
          answer: msg[keys.answerKey],
          key_points: m[keys.keyPointsKey] || [],
          suggested_questions: m[keys.suggestedQuestionsKey] || m.suggested_questions || [],
          page_sources: m[keys.pageSourcesKey] ?? m.page_sources ?? null,
          page_sources_detailed: m[keys.pageSourcesDetailedKey] ?? m.page_sources_detailed ?? null,
          source_type: m[keys.sourceTypeKey] ?? targetMode, // Update source_type to reflect content
          show_curiosity_invite: m[keys.showCuriosityInviteKey] ?? m.show_curiosity_invite ?? false,
          media: m[keys.mediaKey] ?? m.media ?? { images: m.images || [], videos: m.videos || [] },
        };
      }));
      return;
    }

    // 2. Otherwise, ping backend for regeneration
    toast.info(`Generating ${targetMode}...`, { id: `gen-${messageId}` });
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: 'regenerating', display_mode: targetMode } : m));
    setIsRegenerating(true);

    // Create AbortController for regeneration
    regenerationAbortRef.current = new AbortController();

    // Update backend session transiently so semantic cache filter works correctly for this request
    try {
      await updateSessionExplanationMode(currentSessionId, targetMode);
    } catch (e) {
      console.warn("Failed to ping session mode update, generation may use stale cache.", e);
    }

    try {
      // Find the user question that prompted this answer
      const msgIndex = messages.findIndex(m => m.id === messageId);
      const userMsg = messages[msgIndex - 1];
      const questionText = userMsg && userMsg.type === 'user' ? userMsg.content : "Regenerate this explanation";

      const payload = { content: questionText, explanation_mode: targetMode };

      let streamedText = '';
      let pending = '';
      let lastFlush = 0;
      const flush = () => {
        if (!pending) return;
        streamedText += pending;
        pending = '';
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, content: streamedText, answer: streamedText } : m));
      };

      let response = null;
      try {
        response = await sendChatMessageStream(
          currentSessionId,
          payload,
          {
            signal: regenerationAbortRef.current.signal,
            onDelta: (evt) => {
              const text = evt?.text || '';
              if (!text) return;
              pending += text;
              const now = Date.now();
              if (now - lastFlush > 60) {
                lastFlush = now;
                flush();
              }
            }
            ,
            onReplace: (evt) => {
              const text = evt?.text || '';
              if (text === '') return;
              streamedText = text;
              pending = '';
              lastFlush = Date.now();
              setMessages(prev => prev.map(m => m.id === messageId ? { ...m, content: streamedText, answer: streamedText } : m));
            },
          }
        );
      } catch (e) {
        const cancelled = e?.code === 'CANCELLED' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';
        if (cancelled) {
          console.debug('Regeneration was cancelled');
          return;
        }
        throw e;
      } finally {
        flush();
      }

      if (!response) {
        response = await sendChatMessage(currentSessionId, questionText, regenerationAbortRef.current.signal, null, null, targetMode);
      }
      const rawAnswer = response.answer ?? response.content ?? '';
      const answerText = coerceToPlainText(rawAnswer);
      const media = normalizeMediaPayload(response);

      setMessages(prev => prev.map(m => {
        if (m.id !== messageId) return m;
        return {
          ...m,
          status: 'done',
          display_mode: targetMode,
          // Cache per-mode response
          [keys.answerKey]: answerText,
          [keys.keyPointsKey]: response.key_points || [],
          [keys.suggestedQuestionsKey]: response.suggested_questions || [],
          [keys.pageSourcesKey]: response.page_sources || null,
          [keys.pageSourcesDetailedKey]: response.page_sources_detailed || null,
          [keys.sourceTypeKey]: response.source_type || targetMode,
          [keys.mediaKey]: media,
          [keys.showCuriosityInviteKey]: response.show_curiosity_invite || false,
          // Apply as current display
          content: answerText,
          answer: answerText,
          key_points: response.key_points || [],
          suggested_questions: response.suggested_questions || [],
          page_sources: response.page_sources || null,
          page_sources_detailed: response.page_sources_detailed || null,
          source_type: response.source_type || targetMode,
          media: media,
          show_curiosity_invite: response.show_curiosity_invite || false,
        };
      }));
      toast.success(`${targetMode} generated!`, { id: `gen-${messageId}` });
    } catch (error) {
      // Don't show error if cancelled
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.name === 'AbortError' || error.code === 'CANCELLED') {
        console.debug('Regeneration was cancelled');
        return;
      }
      console.error('Failed to generate answer:', error);
      toast.error('Failed to generate answer', { id: `gen-${messageId}` });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: 'done' } : m));

      // Revert session mode if needed
      const explanationModeMap = {
        'Textbook Explanation': 'textbook',
        'Detailed Explanation': 'detailed',
        'Beyond the Book': 'beyond_book'
      };
      updateSessionExplanationMode(currentSessionId, explanationModeMap[explanationType] || 'textbook').catch(console.warn);
    } finally {
      setIsRegenerating(false);
      regenerationAbortRef.current = null;
    }
  };

  // Stop generation handler - handles both sending and regeneration
  const handleStopGeneration = useCallback(() => {
    // Abort regular send
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
      // Remove the last user message (unanswered)
      setMessages(prev => {
        const lastIdx = prev.length - 1;
        if (lastIdx >= 0 && prev[lastIdx].type === 'user') {
          return prev.slice(0, lastIdx);
        }
        return prev;
      });
    }
    // Abort regeneration
    if (regenerationAbortRef.current) {
      regenerationAbortRef.current.abort();
      regenerationAbortRef.current = null;
      setIsRegenerating(false);
      // Revert regenerating messages to done
      setMessages(prev => prev.map(m => m.status === 'regenerating' ? { ...m, status: 'done' } : m));
    }
    toast.info('Generation stopped');
    if (currentSessionId) {
      cancelChatGeneration(currentSessionId).catch(e => console.error("Cancel failed", e));
    }
  }, [currentSessionId]);

  // Start new chat handler
  const handleNewChat = useCallback(async () => {
    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      if (currentSessionId) {
        cancelChatGeneration(currentSessionId).catch(console.warn);
      }
    }

    // Always reset to Textbook mode for a brand-new chat
    setExplanationType('Textbook Explanation');

    // Clear messages and reset session
    setMessages([]);
    setCurrentSessionId(null);
    setIsLoading(false);
    setInputValue('');

    // Create a new session
    if (selectedSubjectId && selectedChapterId) {
      try {
        setSessionLoading(true);
        // Force create a new session
        const session = await getOrCreateSession({
          subject_id: selectedSubjectId,
          chapter_id: selectedChapterId,
          page_context: 'chat',
          explanation_mode: 'textbook'
        }, true); // true = force create new

        setCurrentSessionId(session.session_id);
        try { trackEvent('chat_conversation_started', attachUserProps({ session_id: session.session_id, subject_id: selectedSubjectId, chapter_id: selectedChapterId, page: 'chat' })); } catch (e) { }
        toast.success('Started new chat');
      } catch (error) {
        console.error('Error creating new session:', error);
        toast.error('Failed to create new chat');
      } finally {
        setSessionLoading(false);
      }
    } else {
      toast.info('Please select a subject and chapter');
    }
  }, [selectedSubjectId, selectedChapterId]);

  const handleSendMessage = async (overrideText = null) => {
    // If this function is used directly as an event handler (e.g. onClick={handleSendMessage}),
    // React will pass a SyntheticEvent as the first arg. Treat that as "no override".
    const overrideLooksLikeEvent =
      overrideText &&
      typeof overrideText === 'object' &&
      (typeof overrideText.preventDefault === 'function' || typeof overrideText.stopPropagation === 'function');

    const safeOverrideText = overrideLooksLikeEvent ? null : overrideText;
    const rawToSend = safeOverrideText ?? inputValue ?? '';
    const textToSend = coerceToPlainText(rawToSend).trim();
    if (!textToSend || isLoading || !currentSessionId) {
      if (!currentSessionId) {
        toast.error('Please select a subject and chapter first');
      }
      return;
    }

    // Clear any previous citation highlight when asking a new question
    clearCitationHighlight();

    // If there is selected context (prefilledText), include it as Context in the query
    const contextForQuery = coerceToPlainText(prefilledText).trim();
    const query = contextForQuery ? `Context: "${contextForQuery}"\n\nQuestion: ${textToSend}` : textToSend;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: query,
      timestamp: new Date().toISOString()
    };

    forceScrollNextRef.current = true;
    setMessages(prev => [...prev, userMessage]);
    try { trackEvent('chat_message_sent', attachUserProps({ session_id: currentSessionId, length: textToSend.length, page: 'chat' })); } catch (e) { }
    setInputValue('');
    // Clear the prefilled context once used
    if (prefilledText) setPrefilledText('');
    setIsLoading(true);

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      const explanationModeMap = {
        'Textbook Explanation': 'textbook',
        'Detailed Explanation': 'detailed',
        'Beyond the Book': 'beyond_book'
      };
      const targetMode = explanationModeMap[explanationType] || 'textbook';

      // STREAMING: create a placeholder AI bubble and stream deltas into it.
      const streamingMessageId = `stream_${Date.now()}`;
      const initialAiMessage = {
        id: streamingMessageId,
        type: 'ai',
        content: '',
        status: 'streaming',
        source_type: targetMode,
        timestamp: new Date().toISOString(),
      };
      forceScrollNextRef.current = true;
      setMessages(prev => [...prev, initialAiMessage]);

      // Build payload (matches non-stream endpoint)
      const payload = { content: query, explanation_mode: targetMode };

      let streamedText = '';
      let pending = '';
      let lastFlush = 0;
      const flush = () => {
        if (!pending) return;
        streamedText += pending;
        pending = '';
        forceScrollNextRef.current = true;
        setMessages(prev => prev.map(m => m.id === streamingMessageId ? { ...m, content: streamedText } : m));
      };

      let response = null;
      try {
        response = await sendChatMessageStream(
          currentSessionId,
          payload,
          {
            signal: abortControllerRef.current.signal,
            onDelta: (evt) => {
              const text = evt?.text || '';
              if (!text) return;
              pending += text;
              const now = Date.now();
              if (now - lastFlush > 60) {
                lastFlush = now;
                flush();
              }
            },
            onReplace: (evt) => {
              const text = evt?.text || '';
              if (text === '') return;
              // Replace the whole content with the formatted snapshot.
              streamedText = text;
              pending = '';
              lastFlush = Date.now();
              forceScrollNextRef.current = true;
              setMessages(prev => prev.map(m => m.id === streamingMessageId ? { ...m, content: streamedText } : m));
            },
          }
        );
      } catch (e) {
        // Normalize cancel signals from our streaming client.
        const cancelled = e?.code === 'CANCELLED' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';
        if (cancelled) {
          setMessages(prev => prev.filter(m => m.id !== streamingMessageId));
          return;
        }
        throw e;
      } finally {
        flush();
      }

      // Fallback: if backend didn't stream but returned nothing, use the normal API.
      if (!response) {
        response = await sendChatMessage(
          currentSessionId,
          query,
          abortControllerRef.current.signal,
          null,
          null,
          targetMode
        );
      }

      const rawAnswer = response.answer ?? response.content ?? 'I apologize, but I could not generate a response.';
      const answerText = coerceToPlainText(rawAnswer);

      // Debug mermaid detection
      if (answerText.includes('```mermaid') || answerText.includes('flowchart') || answerText.includes('mermaid')) {
        console.log('🔍 [ChatPage] Found mermaid-like content in answer');
        console.log('📝 First 500 chars:', answerText.substring(0, 500));
      }

      // Use the backend's actual source_type for caching (source of truth)
      // This prevents "cache poisoning" where a textbook answer clobbers a detailed key
      const responseMode = response.source_type || targetMode;

      const aiMessage = {
        id: response.message_id || Date.now() + 1,
        type: 'ai',
        content: answerText,
        key_points: response.key_points || [],
        suggested_questions: response.suggested_questions || [],
        page_sources: response.page_sources || null,
        page_sources_detailed: response.page_sources_detailed || null,
        external_sources: response.external_sources || null,
        source_type: response.source_type || 'beyond_book',
        no_textbook_result: response.no_textbook_result || false,
        show_curiosity_invite: response.show_curiosity_invite || false,
        media: response.media || null, // CRITICAL: Include media (images/videos)
        timestamp: response.created_at || new Date().toISOString(),

        // Cache explicitly for the mode RETURNED by the backend
        [`answer_${responseMode}_v2`]: answerText,
        [`key_points_${responseMode}_v2`]: response.key_points || [],
        [`suggested_questions_${responseMode}_v2`]: response.suggested_questions || [],
        [`page_sources_${responseMode}_v2`]: response.page_sources || null,
        [`page_sources_detailed_${responseMode}_v2`]: response.page_sources_detailed || null,
        [`source_type_${responseMode}_v2`]: response.source_type || 'beyond_book',
        [`media_${responseMode}_v2`]: response.media || null,
        [`images_${responseMode}_v2`]: response.media?.images || [],
        [`videos_${responseMode}_v2`]: response.media?.videos || [],
        [`sources_${responseMode}_v2`]: response.sources || [],
        [`figure_references_${responseMode}_v2`]: response.figure_references || [],
        [`show_curiosity_invite_${responseMode}_v2`]: response.show_curiosity_invite || false,
      };

      // Replace the streaming placeholder with the final canonical message.
      forceScrollNextRef.current = true;
      setMessages(prev => prev.map(m => m.id === streamingMessageId ? { ...aiMessage, status: 'done' } : m));
      try { trackEvent('chat_ai_reply', attachUserProps({ session_id: currentSessionId, source_type: aiMessage.source_type, has_media: !!aiMessage.media, page: 'chat' })); } catch (e) { }

      if (speakNextReplyRef.current) {
        speakNextReplyRef.current = false;
        speakBudleeReply(answerText);
      }

      // Show curiosity invite if flag is set
      if (response.show_curiosity_invite) {
        toast.info('Explore more in Curiosity Centre!', {
          description: 'Check out related videos, podcasts, and resources'
        });
      }
    } catch (error) {
      // Don't show error if request was cancelled
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.name === 'AbortError' || error.code === 'CANCELLED') {
        console.debug('Request was cancelled');
        return;
      }

      speakNextReplyRef.current = false;

      console.error('Chat API error:', error);
      const errMsg = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to get response. Please try again.';

      // Mark the user message as failed (instead of creating an error AI message that pollutes cache)
      setMessages(prev => {
        const lastIdx = prev.length - 1;
        if (lastIdx >= 0 && prev[lastIdx].type === 'user') {
          const updated = [...prev];
          updated[lastIdx] = { ...updated[lastIdx], status: 'error', errorMessage: errMsg };
          return updated;
        }
        return prev;
      });
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  // Keep the ref in sync for voice conversation callbacks
  useEffect(() => {
    handleSendMessageRef.current = handleSendMessage;
  }, [handleSendMessage]);

  // Send a query directly (no editor prefill) using selected context
  const sendDirectMessage = async (questionText, context, anchor = null) => {
    if (!questionText || isLoading) return;
    if (!currentSessionId) {
      toast.error('Please select a subject and chapter first');
      return;
    }

    // Clear any previous citation highlight when asking a new question
    clearCitationHighlight();

    const finalQuery = context ? `Context: "${context}"\n\nQuestion: ${questionText}` : questionText;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: finalQuery,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      const explanationModeMap = {
        'Textbook Explanation': 'textbook',
        'Detailed Explanation': 'detailed',
        'Beyond the Book': 'beyond_book'
      };
      const targetMode = explanationModeMap[explanationType] || 'textbook';

      const streamingMessageId = `stream_${Date.now()}`;
      setMessages(prev => [...prev, {
        id: streamingMessageId,
        type: 'ai',
        content: '',
        status: 'streaming',
        source_type: targetMode,
        timestamp: new Date().toISOString(),
      }]);

      const payload = { content: finalQuery, explanation_mode: targetMode };
      if (anchor) payload.anchor = anchor;

      let streamedText = '';
      let pending = '';
      let lastFlush = 0;
      const flush = () => {
        if (!pending) return;
        streamedText += pending;
        pending = '';
        setMessages(prev => prev.map(m => m.id === streamingMessageId ? { ...m, content: streamedText } : m));
      };

      let response = null;
      try {
        response = await sendChatMessageStream(
          currentSessionId,
          payload,
          {
            signal: abortControllerRef.current.signal,
            onDelta: (evt) => {
              const text = evt?.text || '';
              if (!text) return;
              pending += text;
              const now = Date.now();
              if (now - lastFlush > 60) {
                lastFlush = now;
                flush();
              }
            }
            ,
            onReplace: (evt) => {
              const text = evt?.text || '';
              if (text === '') return;
              streamedText = text;
              pending = '';
              lastFlush = Date.now();
              setMessages(prev => prev.map(m => m.id === streamingMessageId ? { ...m, content: streamedText } : m));
            },
          }
        );
      } catch (e) {
        const cancelled = e?.code === 'CANCELLED' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';
        if (cancelled) {
          setMessages(prev => prev.filter(m => m.id !== streamingMessageId));
          return;
        }
        throw e;
      } finally {
        flush();
      }

      if (!response) {
        response = await sendChatMessage(
          currentSessionId,
          finalQuery,
          abortControllerRef.current.signal,
          null,
          anchor,
          targetMode
        );
      }

      const rawAnswer = response.answer ?? response.content ?? 'I apologize, but I could not generate a response.';
      const answerText = coerceToPlainText(rawAnswer);

      // Use the backend's actual source_type for caching (source of truth)
      const responseMode = response.source_type || targetMode;

      // AI Message object construction (keys already using responseMode from previous partial update)

      const aiMessage = {
        id: response.message_id || Date.now() + 1,
        type: 'ai',
        content: answerText,
        key_points: response.key_points || [],
        suggested_questions: response.suggested_questions || [],
        page_sources: response.page_sources || null,
        page_sources_detailed: response.page_sources_detailed || null,
        external_sources: response.external_sources || null,
        source_type: response.source_type || 'beyond_book',
        no_textbook_result: response.no_textbook_result || false,
        show_curiosity_invite: response.show_curiosity_invite || false,
        media: response.media || null,
        timestamp: response.created_at || new Date().toISOString(),

        // Cache explicitly for the mode RETURNED by the backend
        [`answer_${responseMode}_v2`]: answerText,
        [`key_points_${responseMode}_v2`]: response.key_points || [],
        [`suggested_questions_${responseMode}_v2`]: response.suggested_questions || [],
        [`page_sources_${responseMode}_v2`]: response.page_sources || null,
        [`page_sources_detailed_${responseMode}_v2`]: response.page_sources_detailed || null,
        [`source_type_${responseMode}_v2`]: response.source_type || 'beyond_book',
        [`media_${responseMode}_v2`]: response.media || null,
        [`images_${responseMode}_v2`]: response.media?.images || [],
        [`videos_${responseMode}_v2`]: response.media?.videos || [],
        [`sources_${responseMode}_v2`]: response.sources || [],
        [`figure_references_${responseMode}_v2`]: response.figure_references || [],
        [`show_curiosity_invite_${responseMode}_v2`]: response.show_curiosity_invite || false,
      };

      setMessages(prev => prev.map(m => m.id === streamingMessageId ? { ...aiMessage, status: 'done' } : m));

      if (response.show_curiosity_invite) {
        toast.info('Explore more in Curiosity Centre!', {
          description: 'Check out related videos, podcasts, and resources'
        });
      }
    } catch (error) {
      // Don't show error if request was cancelled
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.name === 'AbortError' || error.code === 'CANCELLED') {
        console.debug('Request was cancelled');
        return;
      }

      console.error('Chat API error:', error);
      const errMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to get response. Please try again.';

      // Mark the user message as failed (instead of creating an error AI message that pollutes cache)
      setMessages(prev => {
        const lastIdx = prev.length - 1;
        if (lastIdx >= 0 && prev[lastIdx].type === 'user') {
          const updated = [...prev];
          updated[lastIdx] = { ...updated[lastIdx], status: 'error', errorMessage: errMsg };
          return updated;
        }
        return prev;
      });
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  // Retry a failed message - removes it and resends the question fresh
  const handleRetryMessage = useCallback((failedMessage) => {
    const questionText = failedMessage.content;
    // Remove the failed message from UI
    setMessages(prev => prev.filter(m => m.id !== failedMessage.id));
    // Re-send the question as a fresh message after state settles
    setTimeout(() => {
      sendDirectMessage(questionText);
    }, 100);
  }, [sendDirectMessage]);

  const handleSuggestedQuery = (query) => {
    setInputValue(coerceToPlainText(query));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Mode switch with confirmation
  const requestModeSwitch = (newMode) => {
    if (newMode === explanationType) return; // same mode, no action
    setPendingModeSwitch({ mode: newMode });
  };

  const confirmModeSwitch = async () => {
    if (!pendingModeSwitch) return;
    const newMode = pendingModeSwitch.mode;
    setPendingModeSwitch(null);
    setExplanationType(newMode);

    if (currentSessionId) {
      try {
        const explanationModeMap = {
          'Textbook Explanation': 'textbook',
          'Detailed Explanation': 'detailed',
          'Beyond the Book': 'beyond_book'
        };
        const modeValue = explanationModeMap[newMode] || 'textbook';
        await updateSessionExplanationMode(currentSessionId, modeValue);

        // RETROACTIVE TOGGLE: If the latest message is an AI response and its mode differs, trigger regeneration
        const latestMsg = messages.length > 0 ? messages[messages.length - 1] : null;
        if (latestMsg && latestMsg.type === 'ai' && latestMsg.source_type !== modeValue) {
          handleMessageModeToggle(latestMsg.id, modeValue);
        }
      } catch (error) {
        console.error('Error updating explanation mode:', error);
        toast.error('Failed to update explanation mode');
      }
    }
  };

  const cancelModeSwitch = () => {
    setPendingModeSwitch(null);
  };

  return (
    <div className="relative flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-hidden">
      {/* Mode switch confirmation dialog */}
      {pendingModeSwitch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <h3 className="text-base font-semibold text-foreground mb-2">
              Switch Explanation Mode?
            </h3>
            <p className="text-sm text-muted-foreground mb-5">
              {pendingModeSwitch.mode === 'Beyond the Book'
                ? 'Explore beyond the textbook with AI-enhanced insights and real-world connections?'
                : pendingModeSwitch.mode === 'Detailed Explanation'
                  ? 'Get a more detailed, in-depth explanation of the topic?'
                  : 'Switch back to textbook-based explanations?'}
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={cancelModeSwitch}
                className="px-4 py-2 text-sm font-medium text-muted-foreground bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmModeSwitch}
                className="px-4 py-2 text-sm font-medium text-white bg-[hsl(var(--primary))] rounded-lg hover:bg-[hsl(var(--teal-glow))] transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Mobile StatsBar - Show at top on mobile only */}
      <div className="flex-shrink-0 pt-2 pb-2 flex justify-center lg:hidden">
        <div className="w-fit">
          <StatsBar />
        </div>
      </div>

      {/* Mobile Action Buttons - Show below StatsBar on mobile only */}
      {selectedSubjectId && selectedChapterId && (
        <div className="flex lg:hidden justify-center gap-2 px-4 pb-0">
          <Button
            size="icon"
            className="h-9 w-9 bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--accent))] hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full"
            onClick={handleNewChat}
            disabled={sessionLoading}
            title="New Chat"
          >
            <Plus className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-semibold h-9 px-3"
            onClick={handleViewSummary}
          >
            <FileText className="w-4 h-4 mr-2" />
            View Summary
          </Button>
          <Button
            size="sm"
            className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-semibold h-9 px-3"
            onClick={() => {
              const lessonData = getLessonData();
              if (lessonData?.pdfUrl) {
                setPdfViewerUrl(lessonData.pdfUrl);
                setPdfViewerPage(1);
                setPdfViewerTitle(lessonData.title);
                setMobilePdfViewerOpen(true);
              }
            }}
          >
            <Eye className="w-4 h-4 mr-2" />
            Show PDF
          </Button>
          <Button
            size="icon"
            className="h-9 w-9 bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--accent))] hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full"
            onClick={() => setShowChatHistory(true)}
            title="Chat History"
          >
            <History className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Mobile PDF Viewer - Popup Modal */}
      <Dialog open={mobilePdfViewerOpen} onOpenChange={setMobilePdfViewerOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none p-0 lg:hidden">
          <PDFSectionViewer
            pdfUrl={pdfViewerUrl}
            authToken={pdfAuthToken}
            chapterId={selectedChapterId}
            initialPage={pdfViewerPage}
            targetSection={pdfViewerSection}
            onClose={() => setMobilePdfViewerOpen(false)}
            highlights={citationHighlights}
            highlightedHighlightId={citationHighlightedId}
            renderTextActions={({ selectedText, selectionAnchor, clearSelection }) => (
              <>
                <Button
                  onClick={() => {
                    console.log('Ask Budlee clicked - selectedText:', selectedText);
                    handleAskBudleeAIWithText(selectedText, null, selectionAnchor);
                    setBudleeAIPrefillAndSend('Please explain and summarize the selected text.');
                    console.log('budleeAIPrefillAndSend set to auto-question');
                  }}
                  size="sm"
                  className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] text-[hsl(var(--main-bg))] font-bold shadow-lg"
                >
                  <img src="/logo.png" alt="Budlee" className="w-5 h-5 mr-2 rounded-full" />
                  Ask Budlee AI
                </Button>
                <Button
                  onClick={() => {
                    handleAskBudleeAIWithText(`Explain: "${selectedText}"`, null, selectionAnchor);
                    setBudleeAIPrefillAndSend('Please explain and summarize the selected text.');
                    clearSelection();
                  }}
                  size="sm"
                  variant="outline"
                  className="bg-white border-gray-200 text-black hover:bg-blue-500 hover:border-blue-500 hover:text-white shadow-lg transition-all"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Explain
                </Button>
                <Button
                  onClick={() => {
                    if (isReadingSelection) {
                      window.speechSynthesis.cancel();
                      setIsReadingSelection(false);
                    } else {
                      if ('speechSynthesis' in window) {
                        window.speechSynthesis.cancel();
                        const utter = new window.SpeechSynthesisUtterance(selectedText);
                        utter.onend = () => setIsReadingSelection(false);
                        utter.onerror = () => setIsReadingSelection(false);
                        window.speechSynthesis.speak(utter);
                        setIsReadingSelection(true);
                      }
                    }
                  }}
                  size="sm"
                  variant="outline"
                  className="bg-white border-gray-200 text-black hover:bg-purple-500 hover:border-purple-500 hover:text-white shadow-lg transition-all"
                >
                  {isReadingSelection ? (
                    <>
                      <Square className="w-4 h-4 mr-2 fill-current" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Mic className="w-4 h-4 mr-2" />
                      Read aloud
                    </>
                  )}
                </Button>
              </>
            )}
          />
        </DialogContent>
      </Dialog>

      {/* Main Content Area - Either Chat or Split-Screen Chapter View */}
      {viewingChapter ? (
        /* Split-Screen: Left Column (TextbookViewer) | Right Column (BudleeAIChatPanel from top) */
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Header: Subject/Chapter Dropdowns - Fixed Height (matching QuestsPage) */}
          <div className="flex-none bg-[hsl(var(--main-bg))] flex flex-col gap-2 sm:gap-3 md:flex-row md:items-center px-4 sm:px-6 py-2 md:py-0 z-50">
            {/* Center: Subject & Chapter Dropdowns in Green Container */}
            <div className="flex-1 flex justify-center min-w-0 order-1 md:order-none">
              <div className="bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] rounded-lg py-3 px-4 flex flex-row flex-wrap items-center gap-2 sm:gap-3 md:gap-4 shadow-2xl border-0 w-full max-w-5xl mx-0 md:mx-6">
                <div className="flex-1 min-w-[150px]">
                  <Select
                    value={selectedSubjectId ? selectedSubjectId.toString() : ''}
                    onValueChange={(value) => {
                      const subject = subjects.find(s => s.id.toString() === value);
                      updateSubject(subject?.id || null, subject?.label || '');
                    }}
                    disabled={loadingSubjects}
                  >
                    <SelectTrigger className="w-full h-10 bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-base focus:ring-1 focus:ring-[hsl(var(--green-bright))] [&>svg]:text-white [&>svg]:opacity-100">
                      <SelectValue placeholder={loadingSubjects ? "Loading subjects..." : "Subject"} />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                      {subjects.map(subject => (
                        <SelectItem
                          key={subject.id}
                          value={subject.id.toString()}
                          className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                        >
                          {subject.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-0">
                  <Select
                    value={selectedChapterId ? selectedChapterId.toString() : ''}
                    onValueChange={(value) => {
                      const chapter = chapters.find(c => c.id.toString() === value);
                      updateChapter(chapter?.id || null, chapter?.label || '');
                    }}
                    disabled={!selectedSubjectId || selectedSubjectId === 'none' || loadingSubjects}
                  >
                    <SelectTrigger className="w-full h-10 bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-sm md:text-base focus:ring-1 focus:ring-[hsl(var(--green-bright))] [&>svg]:text-white [&>svg]:opacity-100">
                      <SelectValue placeholder="Chapter" />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                      {chapters.map(chapter => (
                        <SelectItem
                          key={chapter.id}
                          value={chapter.id.toString()}
                          className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                        >
                          {chapter.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Close Chapter View Button - Unique to ChatPage */}
                {selectedSubject && selectedChapter && (
                  <Button
                    size="sm"
                    className="hidden lg:flex bg-[hsl(var(--main-bg))]/20 border-[hsl(var(--main-bg))]/40 text-white hover:bg-[hsl(var(--main-bg))]/30 h-10 px-3"
                    onClick={handleCloseChapterView}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Close Chapter View
                  </Button>
                )}
              </div>
            </div>

            {/* Right: Stats Bar */}
            <div className="flex items-center flex-shrink-0 ml-4">
              <div className="[&_[data-stats-bar]]:bg-transparent [&_[data-stats-bar]]:border-0 [&_[data-stats-bar]]:shadow-none">
                <StatsBar />
              </div>
            </div>
          </div>

          {/* Content Area: Split between PDF and Chat (mobile stacked, desktop side-by-side) */}
          {/* Responsive split: stacked halves on small screens, side-by-side on desktop */}
          {typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches ? (
            <div className="flex-1 w-full min-h-0 flex flex-col">
              <div className={cn(
                "w-full overflow-auto transition-all duration-300",
                textbookViewerCollapsed ? "h-16" : chatPanelCollapsed ? "flex-1" : "h-1/2"
              )}>
                {/* Collapse/Expand Button for Textbook Viewer */}
                <div className="flex-shrink-0 flex items-center justify-center p-2 bg-[hsl(var(--card-bg))] border-b border-[hsl(var(--card-border))]">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setTextbookViewerCollapsed(!textbookViewerCollapsed)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {textbookViewerCollapsed ? (
                      <>
                        <ChevronDown className="w-3 h-3 mr-2" />
                        Expand Textbook
                      </>
                    ) : (
                      <>
                        <ChevronUp className="w-3 h-3 mr-2" />
                        Collapse Textbook
                      </>
                    )}
                  </Button>
                </div>
                <div className={cn("h-full w-full min-h-0", textbookViewerCollapsed && "hidden")}>
                  {getLessonData() && (
                    <TextbookViewer
                      lesson={getLessonData()}
                      onClose={handleCloseChapterView}
                      onXPEarned={onXPEarned}
                      onAskBudleeAI={handleAskBudleeAIWithText}
                      onViewPdf={() => {
                        const lessonData = getLessonData();
                        if (lessonData?.pdfUrl) {
                          setPdfViewerUrl(lessonData.pdfUrl);
                          setPdfViewerPage(1);
                          setPdfViewerTitle(lessonData.title);
                          setPdfViewerOpen(true);
                        }
                      }}
                      // PDF hidden on mobile - use modal instead
                      showPdf={false}
                      isMobileSplit={true}
                      isFullScreenJourney={mobileJourneyFullscreen}
                      onTogglePdf={() => setMobileShowPdf(v => !v)}
                      onRequestFullJourney={() => setMobileJourneyFullscreen(true)}
                      className="h-full"
                    />
                  )}
                </div>
              </div>

              <div className={cn(
                "w-full overflow-auto border-t border-[hsl(var(--card-border))] transition-all duration-300",
                chatPanelCollapsed ? "h-16" : textbookViewerCollapsed ? "flex-1" : "h-1/2"
              )}>
                {/* Collapse/Expand Button for Chat Panel */}
                <div className="flex-shrink-0 flex items-center justify-center p-2 bg-[hsl(var(--card-bg))] border-b border-[hsl(var(--card-border))]">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setChatPanelCollapsed(!chatPanelCollapsed)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {chatPanelCollapsed ? (
                      <>
                        <ChevronUp className="w-3 h-3 mr-2" />
                        Expand Chat
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-3 h-3 mr-2" />
                        Collapse Chat
                      </>
                    )}
                  </Button>
                </div>
                {!chatPanelCollapsed && (
                  <BudleeAIChatPanel
                    onClose={handleCloseChapterView}
                    prefilledText={budleeAIContextText}
                    onClearPrefilledText={() => {
                      setBudleeAIContextText('');
                      setBudleeAIContextAnchor(null);
                    }}
                    prefilledAnchor={budleeAIContextAnchor}
                    onClearPrefilledAnchor={() => setBudleeAIContextAnchor(null)}
                    prefillInputText={budleeAIPrefillInput}
                    onClearPrefillInput={() => setBudleeAIPrefillInput('')}
                    prefillAndSendText={budleeAIPrefillAndSend}
                    onClearPrefillAndSend={() => setBudleeAIPrefillAndSend('')}
                    subjectId={selectedSubjectId}
                    chapterId={selectedChapterId}
                    onCitationClick={handleCitationClick}
                    onBeforeSend={clearCitationHighlight}
                    // Mobile split props
                    isMobileSplit={true}
                    isFullScreenJourney={mobileJourneyFullscreen}
                    onRequestFullJourney={() => setMobileJourneyFullscreen(true)}
                    onTogglePdf={() => setMobileShowPdf(v => !v)}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-1 w-full flex-col md:flex-row overflow-hidden min-h-0">
              {/* Left Column: TextbookViewer */}
              <div className="flex-1 w-full flex flex-col overflow-hidden h-1/2 md:h-full md:overflow-hidden overflow-auto min-h-0">
                <div className="flex-1 overflow-hidden min-h-0">
                  {getLessonData() && (
                    <TextbookViewer
                      lesson={getLessonData()}
                      onClose={handleCloseChapterView}
                      onXPEarned={onXPEarned}
                      onAskBudleeAI={handleAskBudleeAIWithText}
                      onViewPdf={() => {
                        const lessonData = getLessonData();
                        if (lessonData?.pdfUrl) {
                          setPdfViewerUrl(lessonData.pdfUrl);
                          setPdfViewerPage(1);
                          setPdfViewerTitle(lessonData.title);
                          setPdfViewerOpen(true);
                        }
                      }}
                      // Mobile split props
                      showPdf={mobileShowPdf}
                      isMobileSplit={true}
                      isFullScreenJourney={mobileJourneyFullscreen}
                      onTogglePdf={() => setMobileShowPdf(v => !v)}
                      onRequestFullJourney={() => setMobileJourneyFullscreen(true)}
                    />
                  )}
                </div>
              </div>

              {/* Right Column: BudleeAIChatPanel - Starts from very top of screen */}
              <div className="h-1/2 w-full md:h-full overflow-auto md:overflow-hidden min-h-0">
                <BudleeAIChatPanel
                  onClose={handleCloseChapterView}
                  prefilledText={budleeAIContextText}
                  onClearPrefilledText={() => {
                    setBudleeAIContextText('');
                    setBudleeAIContextAnchor(null);
                  }}
                  prefilledAnchor={budleeAIContextAnchor}
                  onClearPrefilledAnchor={() => setBudleeAIContextAnchor(null)}
                  prefillInputText={budleeAIPrefillInput}
                  onClearPrefillInput={() => setBudleeAIPrefillInput('')}
                  prefillAndSendText={budleeAIPrefillAndSend}
                  onClearPrefillAndSend={() => setBudleeAIPrefillAndSend('')}
                  subjectId={selectedSubjectId}
                  chapterId={selectedChapterId}
                  onCitationClick={handleCitationClick}
                  onBeforeSend={clearCitationHighlight}
                  // Mobile split props
                  isMobileSplit={true}
                  isFullScreenJourney={mobileJourneyFullscreen}
                  onRequestFullJourney={() => setMobileJourneyFullscreen(true)}
                  onTogglePdf={() => setMobileShowPdf(v => !v)}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Regular Chat Interface (optional PDF preview split-pane) */
        <div ref={splitContainerRef} className="flex flex-1 w-full flex-col overflow-hidden">
          {/* Header: Subject/Chapter Dropdowns - Fixed Height (matching QuestsPage) - Hidden on mobile */}
          <div className="hidden md:flex flex-none h-[76px] bg-[hsl(var(--main-bg))] flex items-center px-4 sm:px-6 z-50">
            {/* Left: PDF Preview Toggle (YouLearn-like) - Only show when preview is OFF - Unique to ChatPage */}
            {/* Left: PDF Preview Toggle - Modern Switch - Unique to ChatPage */}
            {selectedChapterId && (
              <div className="hidden lg:flex items-center gap-3 mr-4">
                <div className="flex items-center gap-2 bg-[hsl(var(--card-bg))]/30 backdrop-blur-sm border border-[hsl(var(--card-border))]/40 rounded-full pl-4 pr-2 py-1.5 transition-all hover:bg-[hsl(var(--card-bg))]/50 hover:border-[hsl(var(--card-border))]/60 shadow-sm group cursor-pointer" onClick={() => {
                  const nextState = !showPdfPreview;
                  setShowPdfPreview(nextState);
                  if (nextState) {
                    openPdfPreview({ page: pdfViewerPage || 1 });
                  }
                }}>
                  <BookOpen className={cn("w-4 h-4 transition-colors", showPdfPreview ? "text-[hsl(var(--green-bright))]" : "text-muted-foreground group-hover:text-white")} />
                  <span className={cn(
                    "text-sm font-semibold transition-colors mr-1",
                    showPdfPreview ? "text-white" : "text-muted-foreground group-hover:text-white"
                  )}>
                    Textbook View
                  </span>
                  <Switch
                    checked={showPdfPreview}
                    onCheckedChange={(checked) => {
                      setShowPdfPreview(!!checked);
                      if (checked) {
                        openPdfPreview({ page: pdfViewerPage || 1 });
                      }
                    }}
                    className="data-[state=checked]:bg-[hsl(var(--green-bright))]"
                  />
                </div>
              </div>
            )}

            {/* Center: Subject & Chapter Dropdowns in Green Container */}
            <div className="hidden lg:flex flex-1 justify-center min-w-0">
              <div className="bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] rounded-lg py-3 px-4 flex items-center gap-4 shadow-2xl border-0 w-full max-w-5xl mx-6">
                <div className="flex-1 min-w-0">
                  <Select
                    value={selectedSubjectId ? selectedSubjectId.toString() : ''}
                    onValueChange={(value) => {
                      const subject = subjects.find(s => s.id.toString() === value);
                      updateSubject(subject?.id || null, subject?.label || '');
                    }}
                    disabled={loadingSubjects}
                  >
                    <SelectTrigger className="w-full h-10 bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-base focus:ring-1 focus:ring-[hsl(var(--green-bright))] [&>svg]:text-white [&>svg]:opacity-100">
                      <SelectValue placeholder={loadingSubjects ? "Loading subjects..." : "Subject"} />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                      {subjects.map(subject => (
                        <SelectItem
                          key={subject.id}
                          value={subject.id.toString()}
                          className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                        >
                          {subject.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-0">
                  <Select
                    value={selectedChapterId ? selectedChapterId.toString() : ''}
                    onValueChange={(value) => {
                      const chapter = chapters.find(c => c.id.toString() === value);
                      updateChapter(chapter?.id || null, chapter?.label || '');
                    }}
                    disabled={!selectedSubjectId || selectedSubjectId === 'none' || loadingSubjects}
                  >
                    <SelectTrigger className="w-full h-10 bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-base focus:ring-1 focus:ring-[hsl(var(--green-bright))] [&>svg]:text-white [&>svg]:opacity-100">
                      <SelectValue placeholder="Chapter" />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                      {chapters.map(chapter => (
                        <SelectItem
                          key={chapter.id}
                          value={chapter.id.toString()}
                          className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                        >
                          {chapter.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* View Full Chapter and View Summary Buttons - Unique to ChatPage */}
                {selectedSubjectId && selectedChapterId && (
                  <div className="hidden lg:flex gap-2">
                    <Button
                      size="sm"
                      className="bg-[hsl(var(--main-bg))]/20 border-[hsl(var(--main-bg))]/40 text-white hover:bg-[hsl(var(--main-bg))]/30 h-10 px-3"
                      onClick={handleViewChapter}
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      View Full Chapter
                    </Button>
                    <Button
                      size="sm"
                      className="bg-[hsl(var(--main-bg))]/20 border-[hsl(var(--main-bg))]/40 text-white hover:bg-[hsl(var(--main-bg))]/30 h-10 px-3"
                      onClick={handleViewSummary}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      View Summary
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Stats Bar + Mobile Close Button */}
            <div className="hidden lg:flex order-3 w-full lg:w-auto items-center justify-center lg:justify-end lg:flex-shrink-0 lg:ml-4 gap-2">
              {/* Mobile close button - icon only */}
              {selectedSubject && selectedChapter && (
                <Button
                  size="sm"
                  className="lg:hidden bg-[hsl(var(--main-bg))]/20 border-[hsl(var(--main-bg))]/40 text-white hover:bg-[hsl(var(--main-bg))]/30 h-10 px-2"
                  onClick={handleCloseChapterView}
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              <div className="w-full lg:w-auto [&_[data-stats-bar]]:bg-transparent [&_[data-stats-bar]]:border-0 [&_[data-stats-bar]]:shadow-none">
                <StatsBar />
              </div>
            </div>
          </div>

          {/* Mobile: Subject & Chapter Toggle Button - Show on mobile only, positioned top right */}
          <Button
            onClick={() => setIsSelectionExpanded(!isSelectionExpanded)}
            variant="outline"
            size="icon"
            className="flex lg:hidden fixed right-3 sm:right-4 top-3 sm:top-4 rounded-full bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:from-[hsl(var(--green-bright))]/90 hover:to-[hsl(var(--teal-vivid))]/90 border-0 text-white h-10 sm:h-12 w-10 sm:w-12 z-40"
            title="Select Subject & Chapter"
          >
            <ChevronDown className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform ${isSelectionExpanded ? 'rotate-180' : ''}`} />
          </Button>

          {/* Mobile: Selection Modal Floater - Show when expanded */}
          {isSelectionExpanded && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 lg:hidden">
              <div className="mx-auto w-full px-4 sm:px-6 max-w-2xl sm:max-w-3xl">
                <Card className="bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] border-0 shadow-2xl p-4 sm:p-6">
                  <div className="space-y-3 sm:space-y-4">
                    <h2 className="text-base sm:text-lg font-bold text-[hsl(var(--main-bg))]">
                      Select Subject & Chapter
                    </h2>
                    <div className="flex flex-col gap-3 sm:gap-4">
                      <div className="flex-1 w-full">
                        <Select
                          value={selectedSubjectId ? selectedSubjectId.toString() : ''}
                          onValueChange={(value) => {
                            const subject = subjects.find(s => s.id.toString() === value);
                            updateSubject(subject?.id || null, subject?.label || '');
                          }}
                          disabled={loadingSubjects}
                        >
                          <SelectTrigger className="w-full h-10 sm:h-12 bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-sm sm:text-base focus:ring-1 focus:ring-[hsl(var(--green-bright))] [&>svg]:text-white [&>svg]:opacity-100">
                            <SelectValue placeholder={loadingSubjects ? "Loading subjects..." : "Subject"} />
                          </SelectTrigger>
                          <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                            {subjects.map(subject => (
                              <SelectItem
                                key={subject.id}
                                value={subject.id.toString()}
                                className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                              >
                                {subject.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex-1 w-full">
                        <Select
                          value={selectedChapterId ? selectedChapterId.toString() : ''}
                          onValueChange={(value) => {
                            const chapter = chapters.find(c => c.id.toString() === value);
                            updateChapter(chapter?.id || null, chapter?.label || '');
                          }}
                          disabled={!selectedSubjectId || selectedSubjectId === 'none' || loadingSubjects}
                        >
                          <SelectTrigger className="w-full h-10 sm:h-12 bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-sm sm:text-base focus:ring-1 focus:ring-[hsl(var(--green-bright))] [&>svg]:text-white [&>svg]:opacity-100">
                            <SelectValue placeholder="Chapter" />
                          </SelectTrigger>
                          <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                            {chapters.map(chapter => (
                              <SelectItem
                                key={chapter.id}
                                value={chapter.id.toString()}
                                className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                              >
                                {chapter.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button
                      onClick={() => setIsSelectionExpanded(false)}
                      className="w-full bg-[hsl(var(--main-bg))] hover:bg-[hsl(var(--main-bg))]/90 text-[hsl(var(--green-bright))] font-semibold"
                    >
                      Done
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* Content Area - Starts after header */}
          <div className="flex flex-1 overflow-hidden">
            {showPdfPreview && typeof window !== 'undefined' && window.innerWidth >= 1024 && (
              <div
                data-pdf-preview
                className="h-full p-3 transition-all duration-300 ease-in-out relative group/pdf-panel"
                style={{ width: `${typeof pdfPaneWidth === 'number' ? pdfPaneWidth : 460}px`, minWidth: 280, maxWidth: '70%' }}
              >
                <div className="h-full w-full bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-xl overflow-hidden relative">
                  <PDFSectionViewer
                    key={pdfViewerUrl || selectedChapterId || getLessonData()?.id || 'pdf-viewer'}
                    pdfUrl={pdfViewerUrl || getLessonData()?.pdfUrl}
                    authToken={pdfAuthToken}
                    chapterId={selectedChapterId}
                    initialPage={pdfViewerPage}
                    targetSection={pdfViewerSection}
                    targetFigure={pdfViewerTargetFigure}
                    onClose={() => setShowPdfPreview(false)}
                    highlights={citationHighlights}
                    highlightedHighlightId={citationHighlightedId}
                    renderTextActions={({ selectedText, selectionAnchor, clearSelection }) => (
                      <>
                        <Button
                          onClick={() => {
                            console.log('Ask Budlee clicked - selectedText:', selectedText);
                            // For Ask Budlee, do NOT populate the main chat editor's selected context.
                            // Only populate BudleeAI panel context (used in split-view).
                            handleAskBudleeAIWithText(selectedText, null, selectionAnchor);

                            // If we're in the split-view with BudleeAIChatPanel (viewingChapter), use that panel
                            if (viewingChapter) {
                              // For BudleeAI panel, request auto-send without pre-filling its editor
                              setBudleeAIPrefillAndSend('Please explain and summarize the selected text.');
                              console.log('budleeAIPrefillAndSend set to auto-question');
                            } else {
                              // For main chat page: send directly (no editor prefill)
                              try {
                                sendDirectMessage('Please explain and summarize the selected text.', selectedText, selectionAnchor);
                                console.log('ChatPage: sent direct Ask Budlee request');
                              } catch (e) {
                                console.error('Error sending direct Ask Budlee request', e);
                              }
                            }

                            clearSelection();
                          }}
                          size="sm"
                          className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] text-[hsl(var(--main-bg))] font-bold shadow-lg"
                        >
                          <img src="/logo.png" alt="Budlee" className="w-5 h-5 mr-2 rounded-full" />
                          Ask Budlee AI
                        </Button>
                        <Button
                          onClick={() => {
                            handleAskBudleeAIWithText(`Explain: "${selectedText}"`, null, selectionAnchor);

                            if (viewingChapter) {
                              setBudleeAIPrefillAndSend('Please explain and summarize the selected text.');
                            } else {
                              try {
                                sendDirectMessage('Please explain and summarize the selected text.', selectedText, selectionAnchor);
                              } catch (e) {
                                console.error('Error sending direct Explain request', e);
                              }
                            }

                            clearSelection();
                          }}
                          size="sm"
                          variant="outline"
                          className="bg-white border-gray-200 text-black hover:bg-blue-500 hover:border-blue-500 hover:text-white shadow-lg transition-all"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Explain
                        </Button>
                        <Button
                          onClick={() => {
                            if (isReadingSelection) {
                              window.speechSynthesis.cancel();
                              setIsReadingSelection(false);
                            } else {
                              if ('speechSynthesis' in window) {
                                window.speechSynthesis.cancel();
                                const utter = new window.SpeechSynthesisUtterance(selectedText);
                                utter.onend = () => setIsReadingSelection(false);
                                utter.onerror = () => setIsReadingSelection(false);
                                window.speechSynthesis.speak(utter);
                                setIsReadingSelection(true);
                              }
                            }
                          }}
                          size="sm"
                          variant="outline"
                          className="bg-white border-gray-200 text-black hover:bg-purple-500 hover:border-purple-500 hover:text-white shadow-lg transition-all"
                        >
                          {isReadingSelection ? (
                            <>
                              <Square className="w-4 h-4 mr-2 fill-current" />
                              Stop
                            </>
                          ) : (
                            <>
                              <Mic className="w-4 h-4 mr-2" />
                              Read aloud
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  />
                </div>
              </div>
            )}

            {showPdfPreview && (
              <div
                className="w-2 h-full cursor-col-resize flex items-center justify-center bg-[hsl(var(--main-bg))] hover:bg-[hsl(var(--card-border))]/50 transition-colors z-10"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  isDraggingDividerRef.current = true;
                  document.body.style.cursor = 'col-resize';
                  document.body.style.userSelect = 'none';
                  // Prevent pointer events on other elements during drag
                  document.body.style.pointerEvents = 'none';
                  // Re-enable pointer events on the divider itself
                  e.currentTarget.style.pointerEvents = 'auto';
                }}
                title="Drag to resize"
              >
                <GripVertical className="w-4 h-4 text-muted-foreground" />
              </div>
            )}

            <div className="flex-1 w-full flex flex-col overflow-hidden relative">

              {/* Desktop Floating Buttons */}
              <div className="hidden lg:flex absolute top-8 left-8 z-40 flex flex-col items-start">
                <div className="flex items-center gap-2 group cursor-pointer" onClick={handleNewChat}>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNewChat();
                    }}
                    size="icon"
                    className="h-12 w-12 bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--accent))] hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full animate-float hover:scale-110 relative overflow-visible"
                    title="New Chat"
                    disabled={sessionLoading}
                  >
                    <Plus className="w-6 h-6 relative z-10" />
                    {/* Pulsing glow effect */}
                    <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--accent))] opacity-75 animate-ping"></span>
                  </Button>
                  {/* Expandable Text */}
                  <div className="overflow-hidden transition-all duration-500 ease-in-out max-w-0 group-hover:max-w-[200px] opacity-0 group-hover:opacity-100 flex items-center">
                    <div className="bg-gradient-to-r from-[hsl(var(--green-bright))]/90 to-[hsl(var(--accent))]/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-[hsl(var(--green-bright))]/30">
                      <span className="text-white text-sm font-medium whitespace-nowrap">New Chat</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:flex absolute top-8 right-8 z-40 flex flex-col items-end gap-4">
                {/* Chat History Floating Button with Expandable Text */}
                <div className="flex items-center gap-2 group cursor-pointer" onClick={() => setShowChatHistory(true)}>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowChatHistory(true);
                    }}
                    size="icon"
                    className="h-12 w-12 bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--accent))] hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full animate-float hover:scale-110 relative overflow-visible"
                    title="Chat History"
                  >
                    <History className="w-6 h-6 relative z-10" />
                    {/* Pulsing glow effect */}
                    <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--accent))] opacity-75 animate-ping"></span>
                  </Button>
                  {/* Expandable Text */}
                  <div className="overflow-hidden transition-all duration-500 ease-in-out max-w-0 group-hover:max-w-[200px] opacity-0 group-hover:opacity-100 flex items-center">
                    <div className="bg-gradient-to-r from-[hsl(var(--green-bright))]/90 to-[hsl(var(--accent))]/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-[hsl(var(--green-bright))]/30">
                      <span className="text-sm font-bold text-white whitespace-nowrap tracking-wide">
                        CHAT HISTORY
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chat Area */}
              <div ref={chatScrollContainerRef} className="flex-1 w-full overflow-y-auto pt-0 px-3 pb-3 sm:pt-1 sm:p-4 lg:p-6 overflow-x-hidden">
                {showJumpToLatest && messages.length > 0 && (
                  <div className="sticky bottom-4 z-40 flex justify-center pointer-events-none">
                    <Button
                      type="button"
                      size="sm"
                      className="pointer-events-auto bg-[hsl(var(--green-bright))] text-white shadow-lg hover:bg-[hsl(var(--green-bright))]/90"
                      onClick={() => {
                        const last = Array.isArray(messages) && messages.length ? messages[messages.length - 1] : null;
                        if (!last?.id) return;
                        const el = messageContainerRefById.current.get(last.id);
                        if (!el) return;
                        try {
                          forceScrollNextRef.current = true;
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        } catch { }
                        setShowJumpToLatest(false);
                      }}
                    >
                      Jump to latest
                    </Button>
                  </div>
                )}
                <div className="max-w-4xl sm:max-w-5xl mx-auto w-full h-full flex flex-col overflow-x-hidden">
                  {messages.length === 0 ? (
                    /* Empty State - Reorganized Layout */
                    <div className="space-y-4 pt-0 py-2 sm:space-y-8 sm:py-8">
                      {/* Heading - Icon and Text on Same Line */}
                      <div className="flex items-center justify-center gap-2 sm:gap-4 w-full mt-4 lg:mt-0">
                        <BudleeAIIcon
                          size="w-12 h-12 sm:w-16 sm:h-16"
                          iconSize="w-6 h-6 sm:w-8 sm:h-8"
                          position="relative"
                          showBadge={false}
                          className="animate-float flex-shrink-0"
                        />
                        <h2 className="text-xl sm:text-2xl font-bold budlee-brand-logo text-center block">
                          Talk to Budlee
                        </h2>
                      </div>

                      {/* Input Field - Clean and Simple */}
                      <div className="w-full pr-4 pl-2 sm:px-0">
                        <div className="flex items-end gap-2">
                          {/* Text field with gradient border and icons inside */}
                          <div className="flex-1 max-w-[calc(100vw-6rem)] sm:max-w-none relative p-[2px] rounded-lg bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex flex-col">
                            {/* Context (full-width) + Input row */}
                            {prefilledText && (
                              <div className="w-full bg-[hsl(var(--card-bg))] border-2 border-[hsl(var(--primary))]/30 rounded-lg p-3 mb-2 relative transition transform duration-150 ease-out hover:scale-105 shadow-sm hover:shadow-md">
                                <div className="flex items-start gap-2">
                                  <Sparkles className="w-4 h-4 text-[hsl(var(--primary))] mt-1 flex-shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-xs font-bold text-[hsl(var(--primary))] mb-1">Selected context:</p>
                                    <p className="text-sm text-gray-300 leading-relaxed line-clamp-2">"{coerceToPlainText(prefilledText)}"</p>
                                  </div>
                                  <Button
                                    onClick={() => setPrefilledText('')}
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-foreground"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            )}

                            <div className="w-full flex items-center gap-2 bg-[hsl(var(--card-bg))] rounded-lg px-4 py-3">
                              <textarea
                                ref={chatInputRef}
                                value={inputValue}
                                onChange={(e) => {
                                  setInputValue(String(e.target.value || ''));
                                  try {
                                    const ta = chatInputRef.current;
                                    if (ta) {
                                      ta.style.height = 'auto';
                                      ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
                                    }
                                  } catch { }
                                }}
                                onPaste={(e) => {
                                  // Prevent default paste and manually extract text to avoid [object Object]
                                  e.preventDefault();
                                  const cd = e.clipboardData;
                                  const types = Array.isArray(cd?.types) ? cd.types : [];
                                  const textPlain = cd?.getData('text/plain') || '';
                                  const textText = cd?.getData('text') || '';
                                  const textHtml = cd?.getData('text/html') || '';
                                  const text = textPlain || textText || textHtml || '';
                                  const currentValue = String(inputValue || '');
                                  const textarea = e.target;
                                  const start = textarea.selectionStart || 0;
                                  const end = textarea.selectionEnd || 0;
                                  const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
                                  setInputValue(newValue);
                                  // Set cursor position after pasted text
                                  setTimeout(() => {
                                    textarea.selectionStart = textarea.selectionEnd = start + text.length;
                                  }, 0);
                                }}
                                onKeyDown={handleKeyPress}
                                placeholder="Ask Anything..."
                                className="flex-1 min-h-[32px] max-h-[200px] bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none resize-none text-xs sm:text-base"
                                rows={1}
                              />

                              {/* Microphone icon - Speech to text */}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleSpeechToTextClick}
                                className={cn(
                                  'h-8 w-8 p-0 hover:bg-[hsl(var(--sidebar-hover))] flex-shrink-0',
                                  isSpeechToTextListening && 'animate-pulse'
                                )}
                                title="Speech to text"
                              >
                                <Mic
                                  className={cn(
                                    'w-4 h-4',
                                    isSpeechToTextListening ? 'text-[hsl(var(--destructive))]' : 'text-muted-foreground'
                                  )}
                                />
                              </Button>

                              {/* Voice interaction icon - Voice to voice */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 p-0 hover:bg-[hsl(var(--sidebar-hover))] flex-shrink-0"
                                title="Voice conversation"
                                onClick={handleVoiceConversationClick}
                              >
                                <div className="relative w-8 h-8 rounded-full border-2 border-[hsl(var(--primary))] flex items-center justify-center">
                                  <div className="flex items-center gap-0.5">
                                    <div className="w-0.5 h-2 bg-[hsl(var(--primary))] rounded-full"></div>
                                    <div className="w-0.5 h-3 bg-[hsl(var(--primary))] rounded-full"></div>
                                    <div className="w-0.5 h-4 bg-[hsl(var(--primary))] rounded-full"></div>
                                    <div className="w-0.5 h-3 bg-[hsl(var(--primary))] rounded-full"></div>
                                    <div className="w-0.5 h-2 bg-[hsl(var(--primary))] rounded-full"></div>
                                  </div>
                                </div>
                              </Button>

                              {/* Audio controls (TTS) - next to voice conversation */}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={isTtsPaused ? resumeBudleeAudio : pauseBudleeAudio}
                                disabled={!isTtsSpeaking && !isTtsPaused}
                                className={cn(
                                  'h-8 w-8 p-0 hover:bg-[hsl(var(--sidebar-hover))] flex-shrink-0',
                                  (isTtsSpeaking || isTtsPaused)
                                    ? 'text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/15 rounded-md'
                                    : 'text-muted-foreground'
                                )}
                                title={isTtsPaused ? 'Continue audio' : 'Pause audio'}
                                aria-label={isTtsPaused ? 'Continue audio' : 'Pause audio'}
                              >
                                {isTtsPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={stopBudleeAudio}
                                disabled={!isTtsSpeaking && !isTtsPaused}
                                className={cn(
                                  'h-8 w-8 p-0 hover:bg-[hsl(var(--sidebar-hover))] flex-shrink-0',
                                  (isTtsSpeaking || isTtsPaused)
                                    ? 'text-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10 hover:bg-[hsl(var(--destructive))]/15 rounded-md'
                                    : 'text-muted-foreground'
                                )}
                                title="Stop audio"
                                aria-label="Stop audio"
                              >
                                <Square className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Send/Stop button */}
                          {(isLoading || isRegenerating) ? (
                            <Button
                              onClick={handleStopGeneration}
                              size="icon"
                              className="h-12 w-12 bg-red-500/15 hover:bg-red-500/25 border border-red-400/30 text-red-400 hover:text-red-300 flex-shrink-0 transition-colors animate-pulse"
                              title="Stop generation"
                            >
                              <Square className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              onClick={() => handleSendMessage()}
                              disabled={!inputValue.trim()}
                              size="icon"
                              className="h-12 w-12 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--teal-glow))] text-white flex-shrink-0"
                            >
                              <Send className="w-5 h-5" />
                            </Button>
                          )}
                        </div>

                        {/* Explanation Type Radio Buttons */}
                        <div className="mt-4 flex items-center justify-center gap-2 sm:gap-4">
                          <div className="flex items-center gap-2 sm:gap-4">
                            {[
                              { type: 'Textbook Explanation', icon: BookOpen },
                              { type: 'Detailed Explanation', icon: Layers },
                              { type: 'Beyond the Book', icon: Microscope }
                            ].map(({ type, icon: IconComponent }) => (
                              <label
                                key={type}
                                className={cn(
                                  "flex items-center justify-center gap-1 sm:gap-2 cursor-pointer",
                                  "p-2 sm:p-3 rounded-lg border transition-all",
                                  explanationType === type
                                    ? "bg-[hsl(var(--green-bright))]/20 border-[hsl(var(--green-bright))] text-[hsl(var(--green-bright))]"
                                    : "bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-muted-foreground hover:border-[hsl(var(--green-bright))]/50"
                                )}
                                title={type}
                              >
                                <input
                                  type="radio"
                                  name="explanationType"
                                  value={type}
                                  checked={explanationType === type}
                                  onChange={(e) => requestModeSwitch(e.target.value)}
                                  className="sr-only"
                                />
                                <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" />
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Suggested Queries */}
                      <div className="max-w-2xl mx-auto">
                        <div className="grid grid-cols-2 gap-3">
                          {suggestedQueries.map((query, index) => (
                            <div
                              key={`${selectedChapterId || 'default'}-${index}-${query}`}
                              className={cn(
                                "p-4 rounded-lg cursor-pointer hover:bg-[hsl(var(--card-bg))] transition-all border border-[hsl(var(--card-border))] hover:border-[hsl(var(--primary))] group",
                                topicsAnimating && "animate-fade-in"
                              )}
                              style={{
                                animationDelay: `${index * 100}ms`,
                              }}
                              onClick={() => handleSuggestedQuery(query)}
                            >
                              <p className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">{query}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Chat Messages */
                    <div className="space-y-4 mt-6">
                      {messages.map((message, index) => (
                        <div
                          key={message.id}
                          ref={(node) => {
                            if (node) messageContainerRefById.current.set(message.id, node);
                            else messageContainerRefById.current.delete(message.id);
                          }}
                          className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} items-start gap-3 ${message.type === 'user' && coerceToPlainText(message.content).trim() === 'Please explain and summarize the selected text.'
                            ? 'hidden'
                            : ''
                            }`}
                        >
                          {/* AI Icon - Outside on desktop, inside on mobile */}
                          {message.type === 'ai' && (
                            <div className="hidden sm:flex w-8 h-8 bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--accent))] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                              <Sparkles className="w-4 h-4 text-white" />
                            </div>
                          )}
                          <div
                            className={`w-full sm:max-w-[75%] min-w-0 rounded-2xl p-2 sm:p-3 lg:p-4 shadow-lg relative overflow-hidden ${message.type === 'user'
                              ? 'bg-slate-700 text-white'
                              : 'bg-[hsl(var(--card-bg))] border-2 border-[hsl(var(--card-border))] text-foreground rounded-bl-sm'
                              } ${message.type === 'user' && coerceToPlainText(message.content).trim() === 'Please explain and summarize the selected text.'
                                ? 'hidden'
                                : ''
                              }`}
                          >
                            {/* Chat bubble tail for user messages */}
                            {message.type === 'user' && (
                              <div
                                className="absolute -right-2 bottom-3 w-4 h-4"
                                style={{
                                  backgroundColor: '#334155', // slate-700
                                  transform: 'rotate(45deg)',
                                  borderRadius: '0 0 0 2px',
                                  boxShadow: '2px 2px 4px rgba(0,0,0,0.15)'
                                }}
                              />
                            )}
                            {message.type === 'ai' && (
                              <>
                                <div className="flex items-center gap-2 mb-3">
                                  {/* AI Icon - Inside on mobile */}
                                  <div className="sm:hidden w-6 h-6 bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--accent))] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                                    <Sparkles className="w-3 h-3 text-white" />
                                  </div>
                                  <span className="text-xs font-bold budlee-brand-logo">Budlee AI</span>
                                  {message.source_type && message.status !== 'regenerating' && (
                                    <span className="text-xs px-2 py-0.5 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] rounded-full capitalize font-medium">
                                      {message.source_type.replace('_', ' ')}
                                    </span>
                                  )}
                                </div>
                                {/* Filter Tabs */}
                                {(() => {
                                  if (message.status === 'regenerating') return null;
                                  const images = message.media?.images || [];
                                  const videos = message.media?.videos || [];
                                  const sources = message.page_sources_detailed?.sources || [];
                                  const hasImages = images.length > 0;
                                  const hasVideos = videos.length > 0;
                                  const hasSources = sources.some(s => typeof s === 'string' && s.trim().length > 0);
                                  const showTabs = hasImages || hasVideos || hasSources;
                                  if (!showTabs) return null;

                                  const allowedTabs = new Set([
                                    'all',
                                    ...(hasImages ? ['images'] : []),
                                    ...(hasVideos ? ['videos'] : []),
                                    ...(hasSources ? ['sources'] : []),
                                  ]);
                                  const effectiveTab = allowedTabs.has(message.activeTab || 'all') ? (message.activeTab || 'all') : 'all';

                                  return (
                                    <div className="flex items-center gap-2 mb-4">
                                      <Button
                                        size="sm"
                                        variant={effectiveTab === 'all' ? 'default' : 'outline'}
                                        onClick={() => handleTabChange(message.id, 'all')}
                                        className={effectiveTab === 'all'
                                          ? 'bg-[hsl(var(--primary))] text-white'
                                          : 'bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:bg-[hsl(var(--sidebar-hover))]'
                                        }
                                      >
                                        All
                                      </Button>
                                      {hasImages && (
                                        <Button
                                          size="sm"
                                          variant={effectiveTab === 'images' ? 'default' : 'outline'}
                                          onClick={() => handleTabChange(message.id, 'images')}
                                          className={effectiveTab === 'images'
                                            ? 'bg-[hsl(var(--primary))] text-white'
                                            : 'bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:bg-[hsl(var(--sidebar-hover))]'
                                          }
                                        >
                                          Images
                                        </Button>
                                      )}
                                      {hasVideos && (
                                        <Button
                                          size="sm"
                                          variant={effectiveTab === 'videos' ? 'default' : 'outline'}
                                          onClick={() => handleTabChange(message.id, 'videos')}
                                          className={effectiveTab === 'videos'
                                            ? 'bg-[hsl(var(--primary))] text-white'
                                            : 'bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:bg-[hsl(var(--sidebar-hover))]'
                                          }
                                        >
                                          Videos
                                        </Button>
                                      )}
                                      {hasSources && (
                                        <Button
                                          size="sm"
                                          variant={effectiveTab === 'sources' ? 'default' : 'outline'}
                                          onClick={() => handleTabChange(message.id, 'sources')}
                                          className={effectiveTab === 'sources'
                                            ? 'bg-[hsl(var(--primary))] text-white'
                                            : 'bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:bg-[hsl(var(--sidebar-hover))]'
                                          }
                                        >
                                          Sources
                                        </Button>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* Combined Media Section (Images & Videos) */}
                                {message.status !== 'regenerating' && ((message.activeTab || 'all') === 'all' || message.activeTab === 'images' || message.activeTab === 'videos') && (
                                  (() => {
                                    const images = message.media?.images || [];
                                    const videos = message.media?.videos || [];
                                    const mediaItems = [];

                                    if ((message.activeTab || 'all') === 'all') {
                                      images.forEach(img => mediaItems.push({ type: 'image', data: img }));
                                      videos.forEach(vid => mediaItems.push({ type: 'video', data: vid }));
                                    } else if (message.activeTab === 'images') {
                                      images.forEach(img => mediaItems.push({ type: 'image', data: img }));
                                    } else if (message.activeTab === 'videos') {
                                      videos.forEach(vid => mediaItems.push({ type: 'video', data: vid }));
                                    }

                                    if (mediaItems.length === 0) return null;

                                    return (
                                      <div className="overflow-x-auto pb-4 mb-2">
                                        <div className="flex gap-3">
                                          {mediaItems.map((item, idx) => {
                                            if (item.type === 'image') {
                                              const imgObj = item.data;
                                              const src = typeof imgObj === 'string' ? imgObj : (imgObj?.file_url || '');
                                              if (!src) return null;
                                              return (
                                                <img
                                                  key={`img-${idx}`}
                                                  src={src}
                                                  alt={`Result ${idx + 1}`}
                                                  className="w-32 h-24 object-cover rounded-lg border border-[hsl(var(--card-border))] flex-shrink-0 hover:scale-105 transition-transform cursor-pointer"
                                                  onClick={() => handleMediaClick('image', { file_url: src })}
                                                />
                                              );
                                            } else {
                                              const video = item.data;
                                              const isYoutube = !!video.youtube_url || (video.file_url && video.file_url.includes('youtube.com'));
                                              let videoId = null;
                                              if (isYoutube) {
                                                try {
                                                  const urlObj = new URL(video.youtube_url || video.file_url);
                                                  videoId = urlObj.searchParams.get('v');
                                                } catch (e) { }
                                              }
                                              const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/0.jpg` : null;
                                              const videoTitle = video.title || video.file_name || 'Video';

                                              return (
                                                <div
                                                  key={`vid-${idx}`}
                                                  className="relative w-40 h-24 flex-shrink-0 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg overflow-hidden hover:border-[hsl(var(--primary))] cursor-pointer transition-colors group"
                                                  title={videoTitle}
                                                  onClick={() => handleMediaClick('video', video)}
                                                >
                                                  {thumbnailUrl ? (
                                                    <img src={thumbnailUrl} alt="Video thumbnail" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                                  ) : (
                                                    <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 p-2 flex items-center justify-center">
                                                      <p className="text-[10px] text-white/90 font-medium line-clamp-2 leading-tight text-center">
                                                        {video.section_name || videoTitle}
                                                      </p>
                                                    </div>
                                                  )}

                                                  {/* Play Button Overlay */}
                                                  <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="w-10 h-10 bg-red-600/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                                      <span className="text-white text-sm ml-0.5">▶</span>
                                                    </div>
                                                  </div>

                                                  {/* Youtube Badge */}
                                                  {isYoutube && (
                                                    <div className="absolute bottom-1 right-1">
                                                      <span className="text-[8px] bg-black/80 text-white px-1.5 py-0.5 rounded font-bold tracking-wider">YT</span>
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            }
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })()
                                )}

                                {message.activeTab === 'sources' && (message.page_sources_detailed?.sources || []).length > 0 && (
                                  // Placeholder: keep empty for now; only show tab when sources exist.
                                  <div className="min-h-[1px]" />
                                )}

                                <div className="mb-3">
                                  {message.status === 'regenerating' ? (
                                    <div className="space-y-2 my-4">
                                      <div className="flex items-center justify-center gap-3 py-4 px-4 rounded-lg bg-gradient-to-r from-[hsl(var(--primary))]/10 to-[hsl(var(--primary))]/5 border border-[hsl(var(--primary))]/20 shadow-sm">
                                        <div className="flex items-center gap-1.5">
                                          <div className="w-2.5 h-2.5 bg-[hsl(var(--primary))] rounded-full animate-bounce" />
                                          <div className="w-2.5 h-2.5 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-100" />
                                          <div className="w-2.5 h-2.5 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-200" />
                                        </div>
                                        <span className="text-sm font-semibold text-[hsl(var(--primary))]">
                                          Generating {message.display_mode === 'detailed' ? 'Detailed Explanation' : message.display_mode === 'beyond_book' ? 'Beyond the Book' : 'Textbook Explanation'}...
                                        </span>
                                      </div>
                                      <div className="flex justify-center">
                                        <button
                                          onClick={handleStopGeneration}
                                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-400 bg-red-500/10 border border-red-400/20 rounded-full hover:bg-red-500/20 transition-colors"
                                        >
                                          <Square className="w-3 h-3" />
                                          Stop generating
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <CitedMessageContent
                                      content={parseInteractiveContent(formatAnswerForDisplay(message.content, { streaming: message.status === 'streaming' })).cleanContent}
                                      messageId={message.id || message.timestamp}
                                      pageSources={message.page_sources || []}
                                      autoInjectCitations={true}
                                      onCitationClick={handleCitationClick}
                                      streaming={message.status === 'streaming'}
                                    />
                                  )}
                                  {/* Interactive Simulation (e.g., interactive_diagram) */}
                                  {message.status !== 'regenerating' && (() => {
                                    const { simulation } = parseInteractiveContent(formatAnswerForDisplay(message.content, { streaming: message.status === 'streaming' }));
                                    if (simulation) {
                                      return (
                                        <div className="mt-4 mb-4">
                                          {simulation.title && (
                                            <div className="mb-2">
                                              <div className="text-sm font-semibold text-foreground">{simulation.title}</div>
                                              {simulation.description && (
                                                <div className="text-xs text-muted-foreground">{simulation.description}</div>
                                              )}
                                            </div>
                                          )}
                                          <SimulationRenderer
                                            simulationType={simulation.simulationType}
                                            simulationData={simulation.simulationData}
                                            onComplete={() => { }}
                                          />
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                  {/* Interactive Quiz */}
                                  {message.status !== 'regenerating' && (() => {
                                    const { quizData } = parseInteractiveContent(formatAnswerForDisplay(message.content, { streaming: message.status === 'streaming' }));
                                    if (quizData) {
                                      return (
                                        <div className="mt-4 mb-4">
                                          <InteractiveQuiz data={quizData} />
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>

                                {/* VNEXT Figure/Table References (clickable PDF navigation) - Book-like smooth experience */}
                                {message.figure_references && message.figure_references.length > 0 && (
                                  <div className="mb-3">
                                    <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-2.5 flex items-center gap-1.5">
                                      <span>📖</span>
                                      <span>Referenced Figures & Tables:</span>
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      {message.figure_references.slice(0, 10).map((ref, idx) => (
                                        (() => {
                                          const figureLabel = ref?.reference || ref?.figure || ref?.label || ref?.name || '';
                                          const pageValue = ref?.page ?? null;
                                          return (
                                            <button
                                              key={idx}
                                              onClick={() => {
                                                handleCitationClick({
                                                  page: pageValue,
                                                  figure: figureLabel || null,
                                                  section_name: null,
                                                  highlight: {},
                                                });
                                              }}
                                              className="group px-3 py-2 rounded-lg border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary))]/10 to-[hsl(var(--primary))]/5 hover:from-[hsl(var(--primary))]/20 hover:to-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] text-xs font-medium transition-all duration-300 cursor-pointer flex items-center gap-2 shadow-sm hover:shadow-md hover:scale-105 hover:-translate-y-0.5"
                                              title={`${figureLabel || 'Figure'}${pageValue ? ` - Page ${pageValue}` : ''}${ref?.caption ? `: ${ref.caption}` : ''}`}
                                            >
                                              <span className="font-semibold text-sm">{figureLabel || 'Figure'}</span>
                                              <span className="text-[hsl(var(--primary))]/50 group-hover:text-[hsl(var(--primary))]/70 transition-colors">•</span>
                                              {pageValue ? (
                                                <span className="text-[hsl(var(--primary))]/70 group-hover:text-[hsl(var(--primary))] transition-colors text-xs">Pg {pageValue}</span>
                                              ) : (
                                                <span className="text-[hsl(var(--primary))]/70 group-hover:text-[hsl(var(--primary))] transition-colors text-xs">Open</span>
                                              )}
                                              <span className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-[hsl(var(--primary))]/60">
                                                →
                                              </span>
                                            </button>
                                          );
                                        })()
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {/* Timestamp */}
                                {message.timestamp && message.status !== 'regenerating' && (
                                  <p className="text-xs text-white/90 mt-3 pt-2 border-t border-[hsl(var(--card-border))] text-right">
                                    {new Date(message.timestamp).toLocaleString([], {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                )}

                                {/* Key Points */}
                                {message.key_points && message.key_points.length > 0 && message.status !== 'regenerating' && (
                                  <div className="mb-3">
                                    <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-2">Key Points:</p>
                                    <MarkdownMath
                                      messageId={`${message.id}-keypoints`}
                                      className="text-xs text-muted-foreground [&_p]:my-1 [&_li]:my-0"
                                      pageSources={message.page_sources || []}
                                      autoInjectCitations={true}
                                      onCitationClick={handleCitationClick}
                                    >
                                      {formatAnswerForDisplay(
                                        (message.key_points || [])
                                          .map((p) => String(p || '').trim().replace(/^[-*+•]\s+/, ''))
                                          .filter(Boolean)
                                          .map((p) => `- ${p}`)
                                          .join('\n')
                                      )}
                                    </MarkdownMath>
                                  </div>
                                )}

                                {/* Page Sources */}
                                {message.status !== 'regenerating' && (
                                  (Array.isArray(message.page_sources) && message.page_sources.length > 0) ||
                                  (message.page_sources_detailed && Array.isArray(message.page_sources_detailed?.sources) && message.page_sources_detailed.sources.length > 0)
                                ) && (
                                    <div className="mb-3">
                                      <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-1">Pages:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {(() => {
                                          const detailed = message.page_sources_detailed && typeof message.page_sources_detailed === 'object'
                                            ? message.page_sources_detailed
                                            : null;
                                          const sources = Array.isArray(detailed?.sources) ? detailed.sources : [];

                                          // Prefer pages from sources that were actually used in the answer.
                                          // This avoids showing irrelevant pages that were retrieved but not cited.
                                          const paragraphs = Array.isArray(detailed?.paragraphs) ? detailed.paragraphs : [];
                                          const usedSourceIds = new Set(
                                            paragraphs
                                              .flatMap((p) => (Array.isArray(p?.source_ids) ? p.source_ids : []))
                                              .map((sid) => String(sid))
                                          );
                                          const usedSources = usedSourceIds.size
                                            ? sources.filter((s) => usedSourceIds.has(String(s?.id)))
                                            : sources;

                                          const pagesFromSources = usedSources
                                            .map((s) => Number(s?.page))
                                            .filter((n) => Number.isFinite(n) && n > 0);

                                          const uniqueSorted = (arr) => Array.from(new Set(arr)).sort((a, b) => a - b);
                                          const pages = pagesFromSources.length
                                            ? uniqueSorted(pagesFromSources)
                                            : uniqueSorted((message.page_sources || []).map((p) => Number(p)).filter((n) => Number.isFinite(n) && n > 0));

                                          // Keep a representative source per page so we can (optionally)
                                          // pass along helpful metadata like `section_name` when navigating.
                                          const sourceByPage = new Map();
                                          for (const s of usedSources) {
                                            const p = Number(s?.page);
                                            if (!Number.isFinite(p) || p <= 0) continue;
                                            if (!sourceByPage.has(p)) sourceByPage.set(p, s);
                                          }

                                          if (!pages.length) return null;

                                          return pages.map((page) => (
                                            <button
                                              key={page}
                                              onClick={() => {
                                                const src = sourceByPage.get(page);
                                                // Page buttons should ONLY navigate (no highlight overlay).
                                                // We still use citation-derived pages for accuracy.
                                                clearCitationHighlight();
                                                openPdfPreview({
                                                  page,
                                                  smooth: true,
                                                  sectionName: src?.section_name || null,
                                                });
                                              }}
                                              className="px-2 py-0.5 bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] rounded text-xs hover:bg-[hsl(var(--primary))]/30 transition-colors cursor-pointer"
                                              title="Open Textbook Page"
                                            >
                                              Page {page}
                                            </button>
                                          ));
                                        })()}
                                      </div>
                                    </div>
                                  )}

                                {/* Curiosity Banner */}
                                {(message.show_curiosity_invite || message.source_type === 'beyond_book') && message.status !== 'regenerating' && (
                                  <div className="mb-3 p-3 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border border-purple-500/30 rounded-lg">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex items-start gap-2">
                                        <Sparkles className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                          <p className="text-xs text-purple-200 font-medium leading-relaxed">
                                            {message.curiosity_content_status === 'draft'
                                              ? "Content is being added. Will be available soon..."
                                              : "Want to explore more? Visit the Curiosity Centre!"}
                                          </p>
                                          {message.curiosity_content_status === 'draft' && (
                                            <p className="text-[10px] text-purple-300/80 mt-1 italic leading-relaxed">
                                              We appreciate your curiosity! Keep learning!
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      {message.curiosity_content_status !== 'draft' && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 text-xs text-purple-300 hover:text-white hover:bg-purple-500/20 whitespace-nowrap mt-0"
                                          onClick={() => {
                                            if (message.curiosity_content_id) {
                                              navigate(`/student/curiosity/content/${message.curiosity_content_id}`);
                                            } else {
                                              navigate('/student/curiosity');
                                            }
                                          }}
                                        >
                                          Go to Curiosity Centre <ExternalLink className="w-3 h-3 ml-1" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Switch to Detailed Explanation Explicit Trigger Button */}
                                {message.status !== 'regenerating' && (() => {
                                  const isTextbookCurrent = (message.display_mode || message.source_type) === 'textbook';
                                  const keys = getModeKeys('detailed');
                                  const hasDetailedLocally = !!message[keys.answerKey] || message.source_type === 'detailed';

                                  if (isTextbookCurrent && !hasDetailedLocally) {
                                    return (
                                      <div className="mb-3 pt-3 border-t border-[hsl(var(--card-border))]">
                                        <Button
                                          onClick={() => handleMessageModeToggle(message.id, 'detailed')}
                                          className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-md transition-all duration-300 transform hover:scale-[1.02]"
                                        >
                                          <Layers className="w-4 h-4 mr-2" />
                                          Switch to Detailed Explanation
                                        </Button>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}

                                {/* Mode Navigation Pagination */}
                                {message.status !== 'regenerating' && (
                                  <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-[hsl(var(--card-border))] text-xs text-muted-foreground w-full">
                                    {(() => {
                                      const ALL_MODES = ['textbook', 'detailed', 'beyond_book'];
                                      const displayLabels = { textbook: 'Textbook Content', detailed: 'Detailed Content', beyond_book: 'Beyond the Book' };

                                      // Only include modes that are actively loaded in local memory or natively returned
                                      const availableModes = ALL_MODES.filter(mode => {
                                        if (message.source_type === mode) return true;
                                        const keys = getModeKeys(mode);
                                        return !!message[keys.answerKey];
                                      });

                                      const currentMode = message.display_mode || message.source_type || 'textbook';
                                      if (!availableModes.includes(currentMode)) {
                                        availableModes.push(currentMode);
                                        availableModes.sort((a, b) => ALL_MODES.indexOf(a) - ALL_MODES.indexOf(b));
                                      }

                                      // If only 1 mode is cached, don't show pagination arrows at all
                                      if (availableModes.length < 2) return null;

                                      const currentIndex = availableModes.indexOf(currentMode);
                                      if (currentIndex === -1) return null;

                                      return (
                                        <>
                                          <button
                                            onClick={() => handleMessageModeToggle(message.id, availableModes[currentIndex - 1])}
                                            disabled={currentIndex === 0}
                                            className="p-1.5 hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] rounded-md transition-colors disabled:opacity-30"
                                            title="Previous Explanation Level"
                                          >
                                            <ChevronLeft className="w-4 h-4" />
                                          </button>
                                          <span className="font-semibold text-[10px] uppercase tracking-wider text-center select-none min-w-[120px]">
                                            {displayLabels[currentMode]}
                                          </span>
                                          <button
                                            onClick={() => handleMessageModeToggle(message.id, availableModes[currentIndex + 1])}
                                            disabled={currentIndex === availableModes.length - 1}
                                            className="p-1.5 hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] rounded-md transition-colors disabled:opacity-30"
                                            title="Next Explanation Level"
                                          >
                                            <ChevronRight className="w-4 h-4" />
                                          </button>
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}

                                {/* Suggested Questions */}
                                {message.suggested_questions && message.suggested_questions.length > 0 && message.status !== 'regenerating' && (
                                  <div className="mt-3 pt-3 border-t border-[hsl(var(--card-border))]">
                                    <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-2">Suggested Questions:</p>
                                    <div className="space-y-1">
                                      {message.suggested_questions.map((question, idx) => (
                                        <button
                                          key={idx}
                                          onClick={() => {
                                            setInputValue(question);
                                            handleSendMessage();
                                          }}
                                          className="block w-full text-left text-xs text-[hsl(var(--primary))] hover:text-[hsl(var(--teal-glow))] hover:underline transition-colors"
                                        >
                                          <MarkdownMath
                                            messageId={`${message.id || message.timestamp}-sq-${idx}`}
                                            className="inline text-xs [&_p]:my-0 [&_p]:inline [&_p]:leading-5"
                                          >
                                            {question}
                                          </MarkdownMath>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                            {message.type === 'user' && (
                              <>
                                <div className="flex items-center justify-end gap-2 mb-2">
                                  {/* User Icon - Inside on mobile */}
                                  <div className="sm:hidden w-5 h-5 bg-white/25 rounded-full flex items-center justify-center backdrop-blur-sm">
                                    <MessageCircle className="w-3 h-3 text-white" />
                                  </div>
                                  <span className="text-xs font-bold text-white">{userFullName}</span>
                                </div>
                                <p className="text-xs sm:text-sm whitespace-pre-wrap leading-relaxed text-white font-medium">{coerceToPlainText(message.content)}</p>
                                {/* Timestamp */}
                                {message.timestamp && (
                                  <p className="text-xs text-white/80 mt-3 pt-2 border-t border-white/25 text-right font-medium">
                                    {new Date(message.timestamp).toLocaleString([], {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                )}
                                {/* Error state with retry button */}
                                {message.status === 'error' && (
                                  <div className="mt-3 pt-3 border-t border-red-400/30">
                                    <div className="flex items-center gap-2 text-red-300 text-xs mb-2">
                                      <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                      </svg>
                                      <span className="font-medium">{message.errorMessage || 'Failed to get response'}</span>
                                    </div>
                                    <button
                                      onClick={() => handleRetryMessage(message)}
                                      disabled={isLoading}
                                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-500/20 border border-red-400/30 rounded-full hover:bg-red-500/30 transition-colors disabled:opacity-50"
                                    >
                                      <RotateCcw className="w-3 h-3" />
                                      Retry
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          {/* User Icon - Outside on desktop only */}
                          {message.type === 'user' && (
                            <div className="hidden sm:flex w-8 h-8 bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg border-2 border-white/30">
                              <MessageCircle className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      ))}

                      {isLoading && (
                        <>
                          <div ref={loadingAnchorRef} />
                          <div className="flex justify-start">
                            <div className="max-w-[80%] rounded-lg p-4 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))]">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce" />
                                <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-100" />
                                <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-200" />
                              </div>
                            </div>
                          </div>
                          {/* Floating stop button - always visible while generating */}
                          <div className="flex justify-center mt-2">
                            <button
                              onClick={handleStopGeneration}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-400 bg-red-500/10 border border-red-400/20 rounded-full hover:bg-red-500/20 transition-colors"
                            >
                              <Square className="w-3 h-3" />
                              Stop generating
                            </button>
                          </div>
                        </>
                      )}

                    </div>
                  )}
                </div>
              </div>

              {/* Input Area - Fixed at Bottom (for conversation view) */}
              {messages.length > 0 && (
                <div className="border-t border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] p-3 sm:p-4 lg:p-6 w-full">
                  <div className="max-w-4xl mx-auto w-full">
                    <div className="flex items-end gap-2">
                      {/* Text field with gradient border and icons inside */}
                      <div className="flex-1 relative p-[2px] rounded-lg bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex flex-col">
                        {/* Context (full-width) + Input row */}
                        {prefilledText && (
                          <div className="w-full bg-[hsl(var(--card-bg))] border-2 border-[hsl(var(--primary))]/30 rounded-lg p-2 sm:p-3 mb-2 relative transition transform duration-150 ease-out hover:scale-105 shadow-sm hover:shadow-md">
                            <div className="flex items-start gap-2">
                              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-[hsl(var(--primary))] mt-1 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-xs font-bold text-[hsl(var(--primary))] mb-1">Selected context:</p>
                                <p className="text-xs sm:text-sm text-gray-300 leading-relaxed line-clamp-2">"{coerceToPlainText(prefilledText)}"</p>
                              </div>
                              <Button
                                onClick={() => setPrefilledText('')}
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground hover:text-foreground"
                              >
                                <X className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                            </div>
                          </div>
                        )}

                        <div className="w-full flex items-center gap-1 sm:gap-2 bg-[hsl(var(--card-bg))] rounded-lg px-3 sm:px-4 py-2 sm:py-3">
                          <textarea
                            ref={chatInputRef}
                            value={inputValue}
                            onChange={(e) => {
                              setInputValue(String(e.target.value || ''));
                              try {
                                const ta = chatInputRef.current;
                                if (ta) {
                                  ta.style.height = 'auto';
                                  ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
                                }
                              } catch { }
                            }}
                            onPaste={(e) => {
                              // Prevent default paste and manually extract text to avoid [object Object]
                              e.preventDefault();
                              const cd = e.clipboardData;
                              const types = Array.isArray(cd?.types) ? cd.types : [];
                              const textPlain = cd?.getData('text/plain') || '';
                              const textText = cd?.getData('text') || '';
                              const textHtml = cd?.getData('text/html') || '';
                              const text = textPlain || textText || textHtml || '';
                              const currentValue = String(inputValue || '');
                              const textarea = e.target;
                              const start = textarea.selectionStart || 0;
                              const end = textarea.selectionEnd || 0;
                              const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
                              setInputValue(newValue);
                              // Set cursor position after pasted text
                              setTimeout(() => {
                                textarea.selectionStart = textarea.selectionEnd = start + text.length;
                              }, 0);
                            }}
                            onKeyDown={handleKeyPress}
                            placeholder="Ask Anything..."
                            className="flex-1 min-h-[32px] max-h-[200px] bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none resize-none text-xs sm:text-base"
                            rows={1}
                          />

                          {/* Microphone icon - Speech to text */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleSpeechToTextClick}
                            className={cn(
                              'h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[hsl(var(--sidebar-hover))] flex-shrink-0',
                              isSpeechToTextListening && 'animate-pulse'
                            )}
                            title="Speech to text"
                          >
                            <Mic
                              className={cn(
                                'w-3 h-3 sm:w-4 sm:h-4',
                                isSpeechToTextListening ? 'text-[hsl(var(--destructive))]' : 'text-muted-foreground'
                              )}
                            />
                          </Button>

                          {/* Voice interaction icon - Voice to voice */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[hsl(var(--sidebar-hover))] flex-shrink-0"
                            title="Voice conversation"
                            onClick={handleVoiceConversationClick}
                          >
                            <div className={cn(
                              'relative w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 flex items-center justify-center',
                              isVoiceConversationListening
                                ? 'border-[hsl(var(--destructive))] animate-pulse'
                                : 'border-[hsl(var(--primary))]'
                            )}>
                              <div className="flex items-center gap-0.5">
                                <div className={cn('w-0.5 h-1.5 sm:h-2 rounded-full', isVoiceConversationListening ? 'bg-[hsl(var(--destructive))]' : 'bg-[hsl(var(--primary))]')}></div>
                                <div className={cn('w-0.5 h-2.5 sm:h-3 rounded-full', isVoiceConversationListening ? 'bg-[hsl(var(--destructive))]' : 'bg-[hsl(var(--primary))]')}></div>
                                <div className={cn('w-0.5 h-3.5 sm:h-4 rounded-full', isVoiceConversationListening ? 'bg-[hsl(var(--destructive))]' : 'bg-[hsl(var(--primary))]')}></div>
                                <div className={cn('w-0.5 h-2.5 sm:h-3 rounded-full', isVoiceConversationListening ? 'bg-[hsl(var(--destructive))]' : 'bg-[hsl(var(--primary))]')}></div>
                                <div className={cn('w-0.5 h-1.5 sm:h-2 rounded-full', isVoiceConversationListening ? 'bg-[hsl(var(--destructive))]' : 'bg-[hsl(var(--primary))]')}></div>
                              </div>
                            </div>
                          </Button>

                          {/* Audio controls (TTS) - next to voice conversation */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={isTtsPaused ? resumeBudleeAudio : pauseBudleeAudio}
                            disabled={!isTtsSpeaking && !isTtsPaused}
                            className={cn(
                              'h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[hsl(var(--sidebar-hover))] flex-shrink-0',
                              (isTtsSpeaking || isTtsPaused)
                                ? 'text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/15 rounded-md'
                                : 'text-muted-foreground'
                            )}
                            title={isTtsPaused ? 'Continue audio' : 'Pause audio'}
                            aria-label={isTtsPaused ? 'Continue audio' : 'Pause audio'}
                          >
                            {isTtsPaused ? <Play className="w-3 h-3 sm:w-4 sm:h-4" /> : <Pause className="w-3 h-3 sm:w-4 sm:h-4" />}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={stopBudleeAudio}
                            disabled={!isTtsSpeaking && !isTtsPaused}
                            className={cn(
                              'h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[hsl(var(--sidebar-hover))] flex-shrink-0',
                              (isTtsSpeaking || isTtsPaused)
                                ? 'text-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10 hover:bg-[hsl(var(--destructive))]/15 rounded-md'
                                : 'text-muted-foreground'
                            )}
                            title="Stop audio"
                            aria-label="Stop audio"
                          >
                            <Square className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Send/Stop button */}
                      {(isLoading || isRegenerating) ? (
                        <Button
                          onClick={handleStopGeneration}
                          size="icon"
                          className="h-10 w-10 sm:h-12 sm:w-12 bg-red-500/15 hover:bg-red-500/25 border border-red-400/30 text-red-400 hover:text-red-300 flex-shrink-0 transition-colors animate-pulse"
                          title="Stop generation"
                        >
                          <Square className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleSendMessage()}
                          disabled={!inputValue.trim()}
                          size="icon"
                          className="h-10 w-10 sm:h-12 sm:w-12 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--teal-glow))] text-white flex-shrink-0"
                        >
                          <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                        </Button>
                      )}
                    </div>

                    {/* Explanation Type Radio Buttons */}
                    <div className="mt-3 sm:mt-4 flex items-center justify-center gap-2 sm:gap-4">
                      <div className="flex items-center gap-2 sm:gap-4">
                        {[
                          { type: 'Textbook Explanation', icon: BookOpen },
                          { type: 'Detailed Explanation', icon: Layers },
                          { type: 'Beyond the Book', icon: Microscope }
                        ].map(({ type, icon: IconComponent }) => (
                          <label
                            key={type}
                            className={cn(
                              "flex items-center justify-center gap-1 sm:gap-2 cursor-pointer",
                              "p-2 sm:p-3 rounded-lg border transition-all",
                              explanationType === type
                                ? "bg-[hsl(var(--green-bright))]/20 border-[hsl(var(--green-bright))] text-[hsl(var(--green-bright))]"
                                : "bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-muted-foreground hover:border-[hsl(var(--green-bright))]/50"
                            )}
                            title={type}
                          >
                            <input
                              type="radio"
                              name="explanationType"
                              value={type}
                              checked={explanationType === type}
                              onChange={(e) => requestModeSwitch(e.target.value)}
                              className="sr-only"
                            />
                            <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" />
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg shadow-2xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--card-border))]">
              <h2 className="text-lg font-semibold text-foreground">
                {selectedChapter ? `${selectedChapter} Summary` : 'Chapter Summary'}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowSummaryModal(false);
                  setSummaryText(null);
                  setSummaryError(null);
                }}
                className="hover:bg-[hsl(var(--sidebar-hover))]"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {summaryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-100" />
                    <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-200" />
                  </div>
                  <p className="text-muted-foreground ml-4">Loading summary...</p>
                </div>
              ) : summaryError ? (
                <div className="text-center py-12">
                  <h3 className="text-lg font-semibold text-foreground mb-2">Error Loading Summary</h3>
                  <p className="text-muted-foreground mb-4">{summaryError}</p>
                  <Button
                    onClick={handleViewSummary}
                    className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white"
                  >
                    Try Again
                  </Button>
                </div>
              ) : summaryText ? (
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-4">
                    {selectedChapter ? `${selectedChapter} - ${selectedSubject} Summary` : 'Chapter Summary'}
                  </h3>
                  <div className="prose prose-invert max-w-none">
                    <p className="text-foreground whitespace-pre-wrap">{summaryText}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Summary Available</h3>
                  <p className="text-muted-foreground">
                    Unable to generate summary for this chapter. Please try again later.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t border-[hsl(var(--card-border))]">
              <Button
                onClick={() => {
                  setShowSummaryModal(false);
                  setSummaryText(null);
                  setSummaryError(null);
                }}
                variant="outline"
                className="border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))]"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Chat History Panel Overlay */}
      {showChatHistory && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1" onClick={() => setShowChatHistory(false)} />
          <ChatHistoryPanel
            onClose={() => setShowChatHistory(false)}
            onSelectSession={async (session) => {
              try {
                // Set flags to prevent session initialization useEffect from running
                isLoadingFromHistoryRef.current = true;
                loadingSessionIdRef.current = session.session_id;

                // Load the selected session
                const fullSession = await getChatSession(session.session_id);

                // Set session ID first - this will prevent the useEffect from creating a new session
                setCurrentSessionId(fullSession.session_id);

                // Update subject/chapter from session if available
                if (fullSession.subject) {
                  updateSubject(fullSession.subject, fullSession.subject_name || '');
                }
                if (fullSession.chapter) {
                  updateChapter(fullSession.chapter, fullSession.chapter_name || '');
                }

                // Load messages first
                if (fullSession.messages && fullSession.messages.length > 0) {
                  const formattedMessages = fullSession.messages.map(msg => ({
                    id: msg.message_id,
                    type: msg.role === 'user' ? 'user' : 'ai',
                    content: msg.role === 'user' ? msg.content : (msg.answer || msg.content),
                    key_points: msg.key_points,
                    suggested_questions: msg.suggested_questions,
                    page_sources: msg.page_sources,
                    page_sources_detailed: msg.page_sources_detailed || null,
                    page_sources_detailed: msg.page_sources_detailed || null,
                    page_sources_detailed: msg.page_sources_detailed || null,
                    source_type: msg.source_type,
                    media: msg.media || null, // Include media (images/videos)
                    timestamp: msg.created_at || msg.timestamp
                  }));
                  setMessages(formattedMessages);
                } else {
                  setMessages([]);
                }

                // Set explanation mode from session (do this last to avoid triggering useEffect)
                if (fullSession.explanation_mode) {
                  const explanationModeMap = {
                    'textbook': 'Textbook Explanation',
                    'detailed': 'Detailed Explanation',
                    'beyond_book': 'Beyond the Book'
                  };
                  const mode = explanationModeMap[fullSession.explanation_mode] || 'Textbook Explanation';
                  // Use a longer delay to ensure all state updates are complete
                  setTimeout(() => {
                    setExplanationType(mode);
                    // Keep flags set a bit longer to ensure useEffect doesn't run
                    setTimeout(() => {
                      isLoadingFromHistoryRef.current = false;
                      loadingSessionIdRef.current = null;
                    }, 500);
                  }, 500);
                } else {
                  // Clear flags after a delay to ensure useEffect doesn't run
                  setTimeout(() => {
                    isLoadingFromHistoryRef.current = false;
                    loadingSessionIdRef.current = null;
                  }, 500);
                }

                setShowChatHistory(false);
              } catch (error) {
                console.error('Error loading session:', error);
                toast.error('Failed to load chat session');
                isLoadingFromHistoryRef.current = false;
                loadingSessionIdRef.current = null;
              }
            }}
          />
        </div>
      )}

      {/* Learning Path Modal (reuses LearningPath component) */}
      <Dialog open={showLearningPathModal} onOpenChange={setShowLearningPathModal}>
        <DialogContent className="max-w-6xl h-[90vh] p-0 overflow-hidden bg-[hsl(var(--card-bg))] text-foreground border border-[hsl(var(--card-border))] shadow-2xl [&>button]:hidden">
          <DialogHeader className="px-6 pt-4 pb-2 border-b border-[hsl(var(--card-border))] bg-[hsl(var(--card-bg))]">
            <DialogTitle className="flex items-center justify-between w-full text-base font-semibold text-foreground">
              <span className="text-white">{selectedSubject && selectedChapter ? `${selectedSubject} - ${selectedChapter}` : 'Full Chapter'}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="h-full overflow-hidden bg-[hsl(var(--page-bg))]">
            <LearningPath
              chatMode
              onToggleChatMode={() => setShowLearningPathModal(false)}
              onXPEarned={onXPEarned}
              onJourneyModeChange={() => { }}
              journeyMode={false}
              onAskBudleeAI={onAskBudleeAI}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Inline Media Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={closeMediaModal}>
          <div className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden shadow-2xl border border-[hsl(var(--card-border))]" onClick={e => e.stopPropagation()}>
            <button
              onClick={closeMediaModal}
              className="absolute top-2 right-2 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center justify-center bg-black min-h-[300px] max-h-[80vh]">
              {selectedMedia.type === 'image' ? (
                <img
                  src={typeof selectedMedia.data === 'string' ? selectedMedia.data : selectedMedia.data.file_url}
                  alt="Full view"
                  className="max-w-full max-h-[80vh] object-contain"
                />
              ) : (
                (() => {
                  const video = selectedMedia.data;
                  const isYoutube = !!video.youtube_url || (video.file_url && video.file_url.includes('youtube.com'));
                  let videoId = null;
                  if (isYoutube) {
                    try {
                      const urlObj = new URL(video.youtube_url || video.file_url);
                      videoId = urlObj.searchParams.get('v');
                    } catch (e) { }
                  }

                  if (isYoutube && videoId) {
                    return (
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                        title="Video player"
                        className="w-full aspect-video max-h-[80vh]"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    );
                  } else {
                    return (
                      <video
                        src={video.file_url}
                        controls
                        autoPlay
                        className="max-w-full max-h-[80vh]"
                      />
                    );
                  }
                })()
              )}
            </div>

            {/* Caption/Details */}
            {(selectedMedia.data.section_name || selectedMedia.data.title || selectedMedia.data.file_name || selectedMedia.data.description) && (
              <div className="p-4 bg-[hsl(var(--card-bg))] border-t border-[hsl(var(--card-border))]">
                <h3 className="text-white font-medium text-lg">
                  {selectedMedia.data.section_name || selectedMedia.data.title || selectedMedia.data.file_name}
                </h3>
                {selectedMedia.data.description && (
                  <p className="text-gray-400 text-sm mt-1">{selectedMedia.data.description}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* PDF Viewer Modal (legacy) */}
      <PDFViewerModal
        isOpen={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={pdfViewerUrl}
        title={pdfViewerTitle}
        initialPage={pdfViewerPage}
      />
    </div>
  );
}
